import { Fragment, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/useEntity';
import { PageHeader } from '../../components/ui/PageHeader';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import { Input, Textarea } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { FormRow } from '../../components/ui/FormRow';
import { Spinner } from '../../components/ui/Spinner';
import { fmt } from '../../lib/formatters';
import { avisoSinCoste } from '../../lib/avisoSinCoste';
import { toast, confirmar } from '../../lib/toast';
import { FileIcon } from '../../components/FileIcon';
import { Tabs } from '../../components/ui/Tabs';
import { supabase } from '../../lib/supabase';
import { ESTADOS_CONFIRMADOS, movimientosDeLinea } from '../../../shared/comisiones';
import { resumenPedido } from '../../../shared/resumen-pedido';
import { EntregarPedidoModal } from '../../components/pedido-cliente/EntregarPedidoModal';
import { CAMPOS_FECHA, ETIQUETA_CAMPO, registroFechas, type HistorialFecha } from '../../../shared/registro-fechas';

type LineaRow = {
  id: string;
  orden: number;
  cotizacion_id: string | null;
  producto_id: string | null;
  sku_cliente: string | null;
  pn_ref: string | null;
  descripcion: string | null;
  ean: string | null;
  unidades: number;
  unidades_entregadas: number;
  unidades_pendientes: number;
  precio_unitario: number | null;
  etd: string | null;
  eta: string | null;
  fecha_entrega_solicitada: string | null;
  fecha_entrega_real: string | null;
  /** Fecha estimada de entrega (por defecto la de la cabecera). YYYY-MM-DD. */
  fecha_entrega_estimada: string | null;
  /** Última entrega registrada (o la entrega real de la línea si es posterior). */
  ultima_entrega_fecha: string | null;
  producto_sku: string | null;
  producto_nombre: string | null;
  producto_marca_id: string | null;
  producto_marca_nombre: string | null;
  producto_gama_id: string | null;
  producto_gama_nombre: string | null;
  producto_categoria_id: string | null;
  producto_categoria_nombre: string | null;
  producto_subcategoria_id: string | null;
  producto_subcategoria_nombre: string | null;
  cotizacion_coste_real_eur: number | string | null;
  cotizacion_cdpe_eur: number | string | null;
  cotizacion_coste_game_final_eur: number | string | null;
  cotizacion_pvpr_iva_eur: number | string | null;
  pedido_proveedor_id: string | null;
  pedido_proveedor_estado: string | null;
  pedido_proveedor_numero_seq: number | null;
  pedido_proveedor_numero_approx: string | null;
  pedido_proveedor_pi: string | null;
  pedido_proveedor_eta: string | null;
  precio_compra_usd: number | string | null;
  cancelada: boolean;
  motivo_cancelacion: string | null;
  /** Descuento técnico de la línea (2 = 2 %), ya resuelto por el servidor. */
  dto_tecnico_pct?: number | null;
  /** Por qué no lleva descuento: 'categoria' | 'subcategoria' | 'producto'; null si lo lleva o no hay regla. */
  dto_tecnico_motivo?: string | null;
};

type PedidoClienteFull = {
  id: string;
  cliente_id: string;
  cliente_codigo: string;
  cliente_nombre: string;
  fecha_pedido: string;
  fecha_pedido_confirmado: string | null;
  numero_approx: string | null;
  numero_cliente: string | null;
  pi_proveedor: string | null;
  estado: string;
  estado_nombre: string | null;
  estado_color: string | null;
  notas: string | null;
  /** Fecha dada al marcar el pedido ENTREGADO. */
  fecha_entrega: string | null;
  /** ETD / ETA / entrega estimada del pedido (YYYY-MM-DD); se aplican a las líneas. */
  etd: string | null;
  eta: string | null;
  fecha_entrega_estimada: string | null;
  created_at: string;
  updated_at: string;
  lineas: LineaRow[];
  entregas: EntregaRow[];
  pedidos_proveedor: PedidoProveedorRow[];
};

type EntregaRow = {
  id: string;
  linea_id: string;
  fecha_entrega: string;
  unidades: number;
  notas: string | null;
  created_at: string;
  created_by_email: string | null;
  sku: string | null;
  descripcion: string | null;
};

type PedidoProveedorRow = {
  id: string;
  linea_id: string;
  numero_seq: number | null;
  numero_approx: string | null;
  pi_proveedor: string | null;
  ref_externa: string | null;
  estado: string;
  estado_nombre: string | null;
  estado_color: string | null;
  fecha: string | null;
  unidades: number;
  precio_compra_usd: number | string | null;
  fecha_estimada_llegada: string | null;
  fecha_recepcion: string | null;
  notas: string | null;
  incidencias: string | null;
  email_generado_at: string | null;
  created_at: string;
  updated_at: string;
  proveedor_nombre: string | null;
  producto_sku: string | null;
  producto_nombre: string | null;
  archivos_count: number;
  modificaciones_count: number;
};

type ArchivoRow = {
  id: string;
  tipo: 'pedido_game' | 'pedido_approx' | 'pedido_proveedor' | 'otro';
  storage_path: string;
  nombre_original: string;
  mime_type: string;
  bytes: number;
  notas: string | null;
  created_at: string;
  url: string | null;
};

type ModificacionRow = {
  id: string;
  fecha_hora: string;
  usuario_email: string | null;
  target_table: 'pedidos_cliente' | 'pedidos_cliente_lineas' | 'pedido_cliente_archivos' | 'pedidos';
  target_id: string;
  accion: 'insert' | 'update' | 'delete';
  cambios: Record<string, unknown>;
  observaciones: string | null;
  /** 'proveedor' = entrada del log de un pedido a proveedor enlazado. */
  origen: 'cliente' | 'proveedor';
  pedido_proveedor_numero_seq: number | null;
};

type Condicion = {
  id: string;
  marca_id: string;
  gama_id: string | null;
  categoria_id: string | null;
  subcategoria_id: string | null;
  margen_pct: number | string | null;
  rappel_pct: number | string | null;
  marketing_pct: number | string | null;
  comision_pm_pct: number | string | null;
  financiero_pct: number | string | null;
};

type CotizacionBuscable = {
  id: string;
  fecha: string;
  unidades: number | string | null;
  coste_game_final_eur: number | string | null;
  pvpr_iva_eur: number | string | null;
  ref_proveedor: string | null;
  nombre_propuesto: string | null;
  producto_sku: string | null;
  producto_nombre: string | null;
  categoria_nombre: string | null;
};

const TABS_PEDIDO = ['info', 'rappel', 'proveedor', 'fechas', 'log'] as const;
type TabPedido = (typeof TABS_PEDIDO)[number];

const ESTADOS = [
  { value: 'PDTE_RESPUESTA', label: 'Pdte. respuesta' },
  { value: 'ACEPTADO', label: 'Aceptado' },
  { value: 'EN_PRODUCCION', label: 'En producción' },
  { value: 'EN_CAMINO', label: 'En camino' },
  { value: 'ENTREGADO', label: 'Entregado' },
  { value: 'INCIDENCIA', label: 'Incidencia' },
  { value: 'RECHAZADO', label: 'Rechazado' },
];

export function PedidoClienteDetalle() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const focusLineaId = searchParams.get('focus');
  const tab = TABS_PEDIDO.includes(searchParams.get('tab') as TabPedido) ? (searchParams.get('tab') as TabPedido) : 'info';
  const setTab = (t: string) => {
    const next = new URLSearchParams(searchParams);
    if (t === 'info') next.delete('tab'); else next.set('tab', t);
    setSearchParams(next, { replace: true });
  };

  const pedido = useQuery({
    queryKey: ['pedido-cliente', id],
    queryFn: () => api.get<PedidoClienteFull>(`/pedidos-cliente/${id}/full`),
    enabled: !!id,
  });
  // Cliente del pedido → etiquetas y condiciones (no asumir GAME).
  const clienteCodigo = pedido.data?.cliente_codigo ?? '';
  // Auto-corrección de URL: muchos enlaces apuntan a /game/pedidos/:id de forma
  // hardcodeada. Cuando el pedido es de otro cliente (p.ej. TICNOVA), al cargar
  // redirigimos a /<cliente>/pedidos/:id para que el menú y la sección sean los
  // correctos. Catch-all robusto: vale para cualquier punto de entrada.
  const location = useLocation();
  useEffect(() => {
    if (!clienteCodigo) return;
    const correctBase = clienteCodigo === 'TICNOVA' ? '/ticnova' : '/game';
    if (!location.pathname.startsWith(`${correctBase}/pedidos/`)) {
      navigate(`${correctBase}/pedidos/${id}${location.search}`, { replace: true });
    }
  }, [clienteCodigo, location.pathname, location.search, id, navigate]);

  const archivos = useQuery({
    queryKey: ['pedido-cliente', id, 'archivos'],
    queryFn: () => api.get<{ data: ArchivoRow[]; storage_configured: boolean }>(`/pedidos-cliente/${id}/archivos`),
    enabled: !!id,
  });

  const modificaciones = useQuery({
    queryKey: ['pedido-cliente', id, 'modificaciones'],
    queryFn: () => api.get<{ data: ModificacionRow[] }>(`/pedidos-cliente/${id}/modificaciones`),
    enabled: !!id,
  });

  const condiciones = useQuery({
    queryKey: ['condiciones-comerciales', clienteCodigo, 'pedido-detalle'],
    queryFn: () => api.get<{ data: Condicion[] }>(`/admin/condiciones-comerciales/listado/full?cliente_codigo=${clienteCodigo}`),
    enabled: !!clienteCodigo,
  });

  const [editCab, setEditCab] = useState(false);
  const [entregarPedido, setEntregarPedido] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelMotivo, setCancelMotivo] = useState('');
  const cancelarPedido = useMutation({
    mutationFn: () => api.patch<{ id: string; estado: string }>(
      `/pedidos-cliente/${id}/estado`,
      { estado: 'CANCELADO', motivo_cancelacion: cancelMotivo.trim() || null },
    ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pedido-cliente'] });
      qc.invalidateQueries({ queryKey: ['pedidos-cliente'] });
      qc.invalidateQueries({ queryKey: ['pedidos-cliente-lineas'] });
      setCancelOpen(false);
      setCancelMotivo('');
    },
    onError: (err) => toast.error('No se pudo cancelar el pedido', (err as Error).message),
  });
  const cancelarLinea = useMutation({
    mutationFn: ({ lineaId, cancelada, motivo }: { lineaId: string; cancelada: boolean; motivo?: string }) =>
      api.patch(`/pedidos-cliente/${id}/lineas/${lineaId}`, { cancelada, motivo_cancelacion: cancelada ? (motivo || null) : null }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pedido-cliente', id, 'full'] });
      qc.invalidateQueries({ queryKey: ['pedidos-cliente'] });
    },
  });
  const [addLineaOpen, setAddLineaOpen] = useState(false);
  const [entregaOpen, setEntregaOpen] = useState<string | null>(null);
  const [addLineaManualOpen, setAddLineaManualOpen] = useState(false);
  const [editLineaId, setEditLineaId] = useState<string | null>(null);

  if (pedido.isLoading) return <Spinner />;
  if (pedido.isError || !pedido.data) {
    return (
      <div>
        <PageHeader title="Pedido no encontrado" />
        <Button variant="secondary" onClick={() => navigate(-1)}>← Volver</Button>
      </div>
    );
  }
  const p = pedido.data;
  const subtotal = p.lineas.filter((l) => !l.cancelada).reduce((acc, l) => acc + (Number(l.precio_unitario) || 0) * Math.max(l.unidades, l.unidades_entregadas ?? 0), 0);
  const lineaEnEdicion = editLineaId ? p.lineas.find((l) => l.id === editLineaId) ?? null : null;
  // Registro de fechas: reprogramaciones de ETD/ETA/entrega, del pedido y de
  // cada línea, sacadas del log (shared/registro-fechas.ts).
  const historialFechas = registroFechas(
    modificaciones.data?.data ?? [],
    new Map(p.pedidos_proveedor.map((pp) => [pp.id, pp.linea_id])),
  );
  const lineaFocus = focusLineaId ? p.lineas.find((l) => l.id === focusLineaId) ?? null : null;
  const clearFocus = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('focus');
    setSearchParams(next, { replace: true });
  };

  return (
    <div>
      <PageHeader
        title={`Pedido ${p.cliente_codigo} — ${p.numero_approx ?? p.numero_cliente ?? p.id.slice(0, 8)}`}
        subtitle={
          <>
            <span>{p.cliente_nombre} · {p.lineas.filter((l) => !l.cancelada).length} línea(s) · {fmt.int(p.lineas.filter((l) => !l.cancelada).reduce((a, l) => a + Math.max(l.unidades, l.unidades_entregadas ?? 0), 0))} ud · {fmt.eur(subtotal)}</span>
            <span className="ml-3 text-xs text-slate-400">
              Alta {fmt.date(p.created_at)} · Editado {fmt.datetime(p.updated_at)}
            </span>
          </>
        }
        actions={
          <>
            <Button variant="secondary" onClick={() => navigate(-1)}>← Volver</Button>
            <Button variant="edit" onClick={() => setEditCab(true)}>✏️ Editar cabecera</Button>
            {(ESTADOS_CONFIRMADOS as readonly string[]).includes(p.estado) && p.estado !== 'FINALIZADO'
              && p.lineas.some((l) => !l.cancelada && !entregadaDelTodo(l)) && (
              <Button variant="create" onClick={() => setEntregarPedido(true)} title="Registra la entrega del pedido: todo lo pendiente o parcial por línea, con su fecha.">
                📦 Entregar pedido
              </Button>
            )}
            {p.estado !== 'CANCELADO' && p.estado !== 'FINALIZADO' && (
              <Button
                variant="danger"
                onClick={() => { setCancelMotivo(''); setCancelOpen(true); }}
                title="Cancela este pedido. Se pedirá un motivo y queda registrado en el audit log."
              >
                🚫 Cancelar pedido
              </Button>
            )}
          </>
        }
      />

      <ResumenPedidoBanda lineas={p.lineas} />

      {/* ── Foco en una línea concreta (?focus=<linea_id>) — vista
            del ciclo COTIZACIÓN → PEDIDO PROVEEDOR → PEDIDO CLIENTE. */}
      {lineaFocus && (
        <FocusLineaPanel
          linea={lineaFocus}
          pedido={p}
          onClose={clearFocus}
        />
      )}

      <Tabs
        tabs={[
          { key: 'info', label: 'Información del pedido', badge: `${p.lineas.filter((l) => !l.cancelada).length}` },
          { key: 'rappel', label: 'Rappel / comisiones / provisiones' },
          { key: 'proveedor', label: 'Pedidos a proveedor', badge: `${p.pedidos_proveedor.length}` },
          { key: 'fechas', label: 'Registro de fechas', badge: historialFechas.length ? `${historialFechas.reduce((a, h) => a + h.cambios, 0)}` : undefined },
          { key: 'log', label: 'Log de modificaciones', badge: modificaciones.data ? `${modificaciones.data.data.length}` : undefined },
        ]}
        active={tab}
        onChange={setTab}
      />

      {tab === 'fechas' && (
        <RegistroFechasCard pedido={p} historial={historialFechas} cargando={modificaciones.isLoading} />
      )}

      {tab === 'info' && (
        <>
        {/* Cabecera compacta: identificación + números + notas + los 2
            documentos del lado cliente, todo en una fila. El PI de proveedor ya
            no vive aquí (es por pedido a proveedor). */}
        <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
          <Card title="Identificación">
            <dl className="space-y-1.5 text-sm">
              <Row k="Estado"><Badge color={p.estado_color ?? '#64748b'}>{p.estado_nombre ?? p.estado}</Badge></Row>
              <Row k="Fecha pedido">{fmt.date(p.fecha_pedido)}</Row>
              <Row k="Alta sistema">{fmt.date(p.created_at)}</Row>
              <Row k="Confirmado">{fmt.date(p.fecha_pedido_confirmado)}</Row>
              <Row k="ETD">{fmt.date(p.etd)}</Row>
              <Row k="ETA">{fmt.date(p.eta)}</Row>
              <Row k="Entrega estimada"><span className="font-semibold">{fmt.date(p.fecha_entrega_estimada)}</span></Row>
            </dl>
          </Card>
          <Card title="Números">
            <dl className="space-y-1.5 text-sm">
              <Row k={`Nº ${clienteCodigo}`}><span className="font-mono">{p.numero_cliente ?? '—'}</span></Row>
              <Row k="Nº APPROX"><span className="font-mono">{p.numero_approx ?? '—'}</span></Row>
            </dl>
          </Card>
          <Card title="Notas">
            {p.notas
              ? <p className="whitespace-pre-line text-sm text-slate-700">{p.notas}</p>
              : <p className="text-sm italic text-slate-400">Sin notas.</p>}
          </Card>
          <ArchivoSlot pedidoClienteId={p.id} tipo="pedido_game" label="Pedido cliente a APPROX" archivo={(archivos.data?.data ?? []).find((a) => a.tipo === 'pedido_game') ?? null} />
          <ArchivoSlot pedidoClienteId={p.id} tipo="pedido_approx" label="Pedido APPROX de cliente" archivo={(archivos.data?.data ?? []).find((a) => a.tipo === 'pedido_approx') ?? null} />
        </div>

        {/* Otros documentos sueltos (+ aviso si el storage no está configurado). */}
        <div className="mb-6">
          {!(archivos.data?.storage_configured ?? false) && (
            <p className="mb-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              El almacenamiento de archivos no está configurado en el servidor (SUPABASE_SERVICE_ROLE_KEY).
            </p>
          )}
          <ArchivosOtros pedidoClienteId={p.id} archivos={(archivos.data?.data ?? []).filter((a) => a.tipo === 'otro')} />
        </div>

        <Card
          title={`Líneas (${p.lineas.length})`}
          description="Pulsa una fila para modificarla. En verde, las líneas entregadas del todo."
          actions={
            <>
              <Button variant="create" size="sm" onClick={() => setAddLineaManualOpen(true)}>+ Añadir producto manual</Button>
              <Button variant="secondary" size="sm" onClick={() => setAddLineaOpen(true)}>+ Desde cotización</Button>
            </>
          }
        >
          {p.lineas.length === 0 ? (
            <p className="text-sm italic text-slate-400">Sin líneas. Pulsa &quot;Añadir línea&quot; para empezar.</p>
          ) : (
            <LineasTable
              lineas={p.lineas}
              onOpenEdit={(lineaId) => setEditLineaId(lineaId)}
              onEntregar={(lineaId) => setEntregaOpen(lineaId)}
              onCancelar={(lineaId, cancelada, motivo) => cancelarLinea.mutate({ lineaId, cancelada, motivo })}
            />
          )}
        </Card>

          <div className="mt-6">
            <EntregasCard pedido={p} onRegistrar={() => setEntregaOpen('')} />
          </div>
        </>
      )}

      {tab === 'rappel' && (
        <RappelComisionProvisionCard
          pedido={p}
          condiciones={condiciones.data?.data ?? []}
        />
      )}

      {tab === 'proveedor' && (
        <>
          <PedidosProveedorCard pedido={p} />
          <div className="mt-6">
            <CompraCdpCard lineas={p.lineas} />
          </div>
        </>
      )}

      {tab === 'log' && (
        <ModificacionesCard
          pedido={p}
          modificaciones={modificaciones.data?.data ?? []}
          cargando={modificaciones.isLoading}
        />
      )}

      {editCab && <EditCabeceraModal pedido={p} onClose={() => setEditCab(false)} />}
      {entregarPedido && (
        <EntregarPedidoModal
          pedidoId={p.id}
          titulo={p.numero_cliente ?? p.numero_approx ?? fmt.date(p.fecha_pedido)}
          onClose={() => setEntregarPedido(false)}
        />
      )}

      <Modal
        open={cancelOpen}
        onClose={() => setCancelOpen(false)}
        title="Cancelar pedido"
        size="md"
        footer={
          <>
            <Button variant="secondary" onClick={() => setCancelOpen(false)} disabled={cancelarPedido.isPending}>Volver</Button>
            <Button
              variant="danger"
              onClick={async () => {
                if (!cancelMotivo.trim()) {
                  if (!(await confirmar({ title: '¿Cancelar sin indicar motivo?', message: 'No has indicado motivo de cancelación.', confirmLabel: 'Cancelar igualmente', danger: true }))) return;
                }
                cancelarPedido.mutate();
              }}
              disabled={cancelarPedido.isPending}
            >
              {cancelarPedido.isPending ? 'Cancelando…' : '🚫 Confirmar cancelación'}
            </Button>
          </>
        }
      >
        <div className="space-y-3 text-sm">
          <div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-xs text-rose-900">
            <p className="font-semibold">El pedido pasará a estado CANCELADO (terminal).</p>
            <p className="mt-1">Las líneas no se borran y quedan visibles, pero el pedido no aparecerá en la vista de abiertos.</p>
          </div>
          <FormRow label="Motivo de la cancelación" hint="Texto libre. Queda en el audit log y en el campo motivo_cancelacion.">
            <Textarea
              rows={3}
              value={cancelMotivo}
              onChange={(e) => setCancelMotivo(e.target.value)}
              placeholder="Ej: el cliente cancela tras renegociación, cambio de modelo…"
            />
          </FormRow>
        </div>
      </Modal>
      {addLineaOpen && <AddLineaModal pedidoClienteId={p.id} clienteCodigo={p.cliente_codigo} onClose={() => setAddLineaOpen(false)} />}
      {addLineaManualOpen && (
        <AddLineaManualModal
          pedidoClienteId={p.id}
          clienteCodigo={p.cliente_codigo}
          onClose={() => setAddLineaManualOpen(false)}
        />
      )}
      {entregaOpen !== null && (
        <RegistrarEntregaModal pedido={p} lineaInicial={entregaOpen || null} onClose={() => setEntregaOpen(null)} />
      )}
      {lineaEnEdicion && (
        <EditLineaModal
          pedidoClienteId={p.id}
          linea={lineaEnEdicion}
          onClose={() => setEditLineaId(null)}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Panel de "foco en una línea" — vista del ciclo Cotización → Pedido
// proveedor → Pedido cliente para una línea concreta del pedido.
// ─────────────────────────────────────────────────────────────────────

function FocusLineaPanel({
  linea,
  pedido,
  onClose,
}: {
  linea: LineaRow;
  pedido: PedidoClienteFull;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const generarProveedor = useGenerarPedidosProveedor(pedido.id);
  const subtotal = (Number(linea.precio_unitario) || 0) * Math.max(linea.unidades, linea.unidades_entregadas ?? 0);
  return (
    <div className="mb-6">
      <div className="mb-2 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-900">
          Ciclo de la línea&nbsp;
          <span className="font-mono text-sm text-slate-500">
            {linea.sku_cliente ?? linea.producto_sku ?? linea.id.slice(0, 8)}
          </span>
          {' — '}{linea.producto_nombre ?? linea.descripcion ?? ''}
        </h2>
        <button
          type="button"
          onClick={onClose}
          className="cursor-pointer text-xs text-slate-500 hover:text-slate-700 hover:underline"
        >
          cerrar foco
        </button>
      </div>
      <div className="grid gap-4 lg:grid-cols-3">
        {/* 1) Cotización origen */}
        <Card title="1. Cotización origen">
          {linea.cotizacion_id ? (
            <dl className="space-y-1.5 text-sm">
              <Row k="ID">
                <span className="font-mono text-xs">{linea.cotizacion_id.slice(0, 8)}</span>
              </Row>
              <Row k="CDPe">{fmt.eur(linea.cotizacion_cdpe_eur)}</Row>
              <Row k="Coste real">{fmt.eur(linea.cotizacion_coste_real_eur)}</Row>
              <Row k="Coste final">{fmt.eur(linea.cotizacion_coste_game_final_eur)}</Row>
              <Row k="PVPr c/IVA">{fmt.eur(linea.cotizacion_pvpr_iva_eur)}</Row>
            </dl>
          ) : (
            <p className="text-sm italic text-slate-400">Esta línea no se generó desde una cotización.</p>
          )}
        </Card>

        {/* 2) Pedido a proveedor */}
        <Card title="2. Pedido a proveedor">
          {linea.pedido_proveedor_id ? (
            <dl className="space-y-1.5 text-sm">
              <Row k="ID">
                <span
                  onClick={() => navigate(`/proveedores/pedidos/${linea.pedido_proveedor_id}`)}
                  className="cursor-pointer font-mono text-xs text-blue-600 hover:underline"
                  role="link"
                >
                  {linea.pedido_proveedor_id.slice(0, 8)}
                </span>
              </Row>
              <Row k="Estado">
                <Badge color="#64748b">{linea.pedido_proveedor_estado ?? '—'}</Badge>
              </Row>
              <Row k="Precio compra">{fmt.usd(linea.precio_compra_usd)}</Row>
              <Row k="Unidades">{fmt.int(Math.max(linea.unidades, linea.unidades_entregadas ?? 0))}</Row>
            </dl>
          ) : (
            <div className="space-y-3">
              <p className="text-sm italic text-slate-400">
                Aún no hay pedido a proveedor. Genéralo ahora (un pedido por producto, al proveedor de cada uno) o se generará al pasar el pedido cliente a <em>Aceptado</em>.
              </p>
              <Button
                variant="create"
                onClick={() => generarProveedor.mutate()}
                disabled={generarProveedor.isPending}
                title="Crea un pedido a proveedor por cada producto del pedido cliente (manteniendo la trazabilidad cotización → pedido cliente → pedido proveedor)."
              >
                {generarProveedor.isPending ? 'Generando…' : '⊕ Generar pedido a proveedor'}
              </Button>
            </div>
          )}
        </Card>

        {/* 3) Pedido de cliente (línea + cabecera) */}
        <Card title="3. Pedido de cliente">
          <dl className="space-y-1.5 text-sm">
            <Row k={`Nº ${pedido.cliente_codigo}`}><span className="font-mono">{pedido.numero_cliente ?? '—'}</span></Row>
            <Row k="Nº APPROX"><span className="font-mono">{pedido.numero_approx ?? '—'}</span></Row>
            <Row k="Fecha pedido">{fmt.date(pedido.fecha_pedido)}</Row>
            <Row k="Estado pedido">
              <Badge color={pedido.estado_color ?? '#64748b'}>{pedido.estado_nombre ?? pedido.estado}</Badge>
            </Row>
            <Row k="Unidades línea">
              {(() => {
                const ef = Math.max(linea.unidades, linea.unidades_entregadas ?? 0);
                const planned = linea.unidades;
                if (ef !== planned) {
                  return (
                    <span>
                      <span className="font-semibold">{fmt.int(ef)}</span>
                      <span className="ml-2 text-xs text-amber-700">({fmt.int(planned)} previstas + {fmt.int(ef - planned)} sobre-entrega)</span>
                    </span>
                  );
                }
                return fmt.int(planned);
              })()}
            </Row>
            <Row k="Precio unit.">{fmt.eur(linea.precio_unitario)}</Row>
            <Row k="Subtotal línea"><span className="font-semibold">{fmt.eur(subtotal)}</span></Row>
            <Row k="Entrega solic.">{fmt.date(linea.fecha_entrega_solicitada)}</Row>
            <Row k="ETD / ETA">{fmt.date(linea.etd) ?? '—'} / {fmt.date(linea.eta) ?? '—'}</Row>
          </dl>
        </Card>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Documentos (adjuntos)
// ─────────────────────────────────────────────────────────────────────

// Documentos del lado cliente: "Pedido cliente a APPROX" (pedido_game) y
// "Pedido APPROX de cliente" (pedido_approx). Los del lado proveedor (Pedido
// APPROX a proveedor y PI de proveedor) viven en cada pedido a proveedor.
// Se renderizan como slots sueltos en la cabecera del pedido.

function ArchivoSlot({
  pedidoClienteId,
  tipo,
  label,
  archivo,
}: {
  pedidoClienteId: string;
  tipo: ArchivoRow['tipo'];
  label: string;
  archivo: ArchivoRow | null;
}) {
  const qc = useQueryClient();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const upload = useMutation({
    mutationFn: async (file: File) => {
      const { data: sess } = await supabase.auth.getSession();
      const token = sess.session?.access_token;
      const fd = new FormData();
      fd.append('file', file);
      fd.append('tipo', tipo);
      const res = await fetch(`/api/pedidos-cliente/${pedidoClienteId}/archivos`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        body: fd,
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(`Upload falló (${res.status}): ${txt}`);
      }
      return res.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pedido-cliente', pedidoClienteId, 'archivos'] }),
  });
  const borrar = useMutation({
    mutationFn: (archivoId: string) => api.delete(`/pedidos-cliente/${pedidoClienteId}/archivos/${archivoId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pedido-cliente', pedidoClienteId, 'archivos'] }),
  });

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); if (!dragOver) setDragOver(true); }}
      onDragLeave={(e) => { e.preventDefault(); setDragOver(false); }}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        const f = e.dataTransfer.files?.[0];
        if (f) upload.mutate(f);
      }}
      className={`flex flex-col items-center gap-2 rounded-lg border p-3 transition ${dragOver ? 'border-sky-400 bg-sky-50 ring-2 ring-sky-300' : archivo ? 'border-slate-300 bg-white' : 'border-dashed border-slate-300 bg-slate-50'}`}
    >
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-600">{label}</p>
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) upload.mutate(f);
          if (inputRef.current) inputRef.current.value = '';
        }}
      />
      {archivo ? (
        <>
          {archivo.url ? (
            <a href={archivo.url} target="_blank" rel="noreferrer" className="block cursor-pointer">
              <FileIcon mime={archivo.mime_type} name={archivo.nombre_original} size={42} />
            </a>
          ) : (
            <FileIcon mime={archivo.mime_type} name={archivo.nombre_original} size={42} />
          )}
          <p className="line-clamp-2 text-center text-xs text-slate-700" title={archivo.nombre_original}>
            {archivo.nombre_original}
          </p>
          <p className="text-[10px] text-slate-400">
            {(archivo.bytes / 1024).toFixed(0)} KB · {fmt.date(archivo.created_at)}
          </p>
          <div className="flex gap-1">
            <Button size="sm" variant="ghost" onClick={() => inputRef.current?.click()} disabled={upload.isPending}>
              Sustituir
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="text-rose-600"
              onClick={async () => { if (await confirmar({ title: '¿Eliminar archivo?', confirmLabel: 'Eliminar', danger: true })) borrar.mutate(archivo.id); }}
              disabled={borrar.isPending}
            >
              ×
            </Button>
          </div>
        </>
      ) : (
        <>
          <FileIcon size={42} />
          <p className="text-center text-xs italic text-slate-400">{dragOver ? 'Suelta el archivo para adjuntarlo' : 'Sin archivo · arrastra el PDF aquí'}</p>
          <Button size="sm" onClick={() => inputRef.current?.click()} disabled={upload.isPending}>
            {upload.isPending ? 'Subiendo…' : '+ Subir'}
          </Button>
        </>
      )}
      {upload.error && (
        <p className="text-[10px] text-rose-600">{(upload.error as Error).message}</p>
      )}
    </div>
  );
}

function ArchivosOtros({
  pedidoClienteId,
  archivos,
}: {
  pedidoClienteId: string;
  archivos: ArchivoRow[];
}) {
  const qc = useQueryClient();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const upload = useMutation({
    mutationFn: async (file: File) => {
      const { data: sess } = await supabase.auth.getSession();
      const token = sess.session?.access_token;
      const fd = new FormData();
      fd.append('file', file);
      fd.append('tipo', 'otro');
      const res = await fetch(`/api/pedidos-cliente/${pedidoClienteId}/archivos`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        body: fd,
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(`Upload falló (${res.status}): ${txt}`);
      }
      return res.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pedido-cliente', pedidoClienteId, 'archivos'] }),
  });
  const borrar = useMutation({
    mutationFn: (archivoId: string) => api.delete(`/pedidos-cliente/${pedidoClienteId}/archivos/${archivoId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pedido-cliente', pedidoClienteId, 'archivos'] }),
  });

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); if (!dragOver) setDragOver(true); }}
      onDragLeave={(e) => { e.preventDefault(); setDragOver(false); }}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        const files = Array.from(e.dataTransfer.files ?? []);
        files.forEach((f) => upload.mutate(f));
      }}
      className={`mt-4 rounded-lg border-t pt-3 transition ${dragOver ? 'border border-dashed border-sky-400 bg-sky-50 ring-2 ring-sky-300' : 'border-slate-200'}`}
    >
      <div className="mb-2 flex items-center justify-between px-1">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-600">Otros documentos ({archivos.length}) <span className="font-normal normal-case text-slate-400">· arrastra aquí para adjuntar</span></p>
        <input
          ref={inputRef}
          type="file"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) upload.mutate(f);
            if (inputRef.current) inputRef.current.value = '';
          }}
        />
        <Button size="sm" variant="ghost" onClick={() => inputRef.current?.click()} disabled={upload.isPending}>
          {upload.isPending ? 'Subiendo…' : '+ Subir'}
        </Button>
      </div>
      {archivos.length === 0 ? (
        <p className="text-xs italic text-slate-400">Sin archivos adicionales.</p>
      ) : (
        <ul className="flex flex-wrap gap-3">
          {archivos.map((a) => (
            <li key={a.id} className="flex items-center gap-2 rounded border border-slate-200 px-2 py-1">
              <FileIcon mime={a.mime_type} name={a.nombre_original} size={28} />
              {a.url ? (
                <a href={a.url} target="_blank" rel="noreferrer" className="cursor-pointer text-xs text-blue-600 hover:underline">{a.nombre_original}</a>
              ) : (
                <span className="text-xs">{a.nombre_original}</span>
              )}
              <button
                type="button"
                onClick={async () => { if (await confirmar({ title: '¿Eliminar archivo?', confirmLabel: 'Eliminar', danger: true })) borrar.mutate(a.id); }}
                className="cursor-pointer text-xs text-rose-600 hover:text-rose-800"
              >×</button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Rappel / Comisión / Provisión
// ─────────────────────────────────────────────────────────────────────

function findCondicionLinea(l: LineaRow, lista: Condicion[]): Condicion | null {
  if (!l.producto_marca_id) return null;
  const candidatas = lista
    .filter((c) => c.marca_id === l.producto_marca_id)
    .map((c) => {
      let score = 0;
      if (c.subcategoria_id === l.producto_subcategoria_id) score += 8;
      else if (c.subcategoria_id !== null) return { c, score: -1 };
      if (c.categoria_id === l.producto_categoria_id) score += 4;
      else if (c.categoria_id !== null) return { c, score: -1 };
      if (c.gama_id === l.producto_gama_id) score += 2;
      else if (c.gama_id !== null) return { c, score: -1 };
      return { c, score };
    })
    .filter((x) => x.score >= 0)
    .sort((a, b) => b.score - a.score);
  return candidatas[0]?.c ?? null;
}

// Generado = sobre lo entregado (se devenga en la fecha de entrega, misma
// regla que /comisiones y /rappel: shared/comisiones.ts). Pendiente = lo que
// falta por entregar. Un pedido sin confirmar aún no genera nada: todo es
// previsión.
const MOTIVO_SIN_DTO: Record<string, string> = {
  categoria: 'Sin descuento técnico: categoría excluida (monitores)',
  subcategoria: 'Sin descuento técnico: subcategoría excluida (fuentes de alimentación de componentes)',
  producto: 'Sin descuento técnico: marcado en el producto (p. ej. fuente externa de portátil)',
};

function RappelComisionProvisionCard({ pedido, condiciones }: { pedido: PedidoClienteFull; condiciones: Condicion[] }) {
  const confirmado = (ESTADOS_CONFIRMADOS as readonly string[]).includes(pedido.estado);
  const filas = useMemo(() => {
    const num = (v: unknown) => Number(v ?? 0) || 0;
    return pedido.lineas.filter((l) => !l.cancelada).map((l) => {
      const c = findCondicionLinea(l, condiciones);
      const subtotal = (Number(l.precio_unitario) || 0) * Math.max(l.unidades, l.unidades_entregadas ?? 0);
      const movs = movimientosDeLinea({
        linea_id: l.id,
        pedido_id: pedido.id,
        pedido_estado: pedido.estado,
        pedido_fecha_entrega: pedido.fecha_entrega,
        unidades: l.unidades,
        unidades_entregadas: l.unidades_entregadas,
        precio_unitario: l.precio_unitario,
        eta: l.pedido_proveedor_eta ?? l.eta,
        fecha_solicitada: l.fecha_entrega_solicitada,
        fecha_entrega_real: l.fecha_entrega_real,
        entregas: pedido.entregas.filter((e) => e.linea_id === l.id).map((e) => ({ fecha: e.fecha_entrega, unidades: e.unidades })),
        comision_pct: null,
        dto_tecnico_pct: l.dto_tecnico_pct,
      });
      const baseGenerada = movs.filter((m) => m.tipo === 'devengada').reduce((a, m) => a + m.base_eur, 0);
      // Bases NETAS del descuento técnico (se descuenta en factura antes del
      // rappel y la comisión).
      const dtoPct = num(l.dto_tecnico_pct);
      const dto = subtotal * (dtoPct / 100);
      const basePendiente = confirmado ? movs.filter((m) => m.tipo === 'estimada').reduce((a, m) => a + m.base_eur, 0) : subtotal - dto;
      const calc = (pct: number | string | null | undefined) => (c && pct != null
        ? { pct: num(pct), generado: baseGenerada * (num(pct) / 100), pendiente: basePendiente * (num(pct) / 100) }
        : null);
      return {
        linea: l, cond: c, subtotal, dtoPct, dto, baseGenerada, basePendiente,
        rappel: calc(c?.rappel_pct), comision: calc(c?.comision_pm_pct), provision: calc(c?.financiero_pct),
      };
    });
  }, [pedido, condiciones, confirmado]);

  const suma = (f: (x: (typeof filas)[number]) => number) => filas.reduce((a, x) => a + f(x), 0);
  const totales = (['rappel', 'comision', 'provision'] as const).map((k) => ({
    k,
    generado: suma((f) => f[k]?.generado ?? 0),
    pendiente: suma((f) => f[k]?.pendiente ?? 0),
  }));
  const sinCondicion = filas.filter((f) => !f.cond).length;
  const TITULO = { rappel: 'Rappel', comision: 'Comisión PM', provision: 'Provisión (financiero)' } as const;

  const Celda = ({ v }: { v: { pct: number; generado: number; pendiente: number } | null }) => (
    v == null
      ? <><td className="px-2 py-1.5 text-right text-xs text-slate-300">—</td><td className="px-2 py-1.5 text-right text-xs text-slate-300">—</td><td className="px-2 py-1.5 text-right text-xs text-slate-300">—</td></>
      : (
        <>
          <td className="px-2 py-1.5 text-right text-xs">{fmt.pct(v.pct)}</td>
          <td className="px-2 py-1.5 text-right font-mono text-xs font-semibold text-emerald-700">{fmt.eur(v.generado)}</td>
          <td className="px-2 py-1.5 text-right font-mono text-xs text-slate-500">{fmt.eur(v.pendiente)}</td>
        </>
      )
  );

  return (
    <div className="space-y-4">
      {!confirmado && (
        <p className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          El pedido aún no está confirmado ({pedido.estado_nombre ?? pedido.estado}): no ha generado rappel, comisión ni provisión. Lo que se ve es la previsión si se acepta y se entrega entero.
        </p>
      )}
      <div className="grid gap-3 sm:grid-cols-3">
        {totales.map((t) => (
          <div key={t.k} className="rounded-lg border border-slate-200 bg-white p-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{TITULO[t.k]}</p>
            <p className="mt-1 font-mono text-xl font-semibold text-emerald-700">{fmt.eur(t.generado)}</p>
            <p className="text-xs text-slate-500">generado · {fmt.eur(t.pendiente)} pendiente · {fmt.eur(t.generado + t.pendiente)} total</p>
          </div>
        ))}
      </div>
      <Card
        title="Detalle por línea"
        description="Condición comercial más específica del producto (marca › gama › categoría › subcategoría). Las bases van netas del descuento técnico, que se descuenta en factura antes del rappel y la comisión. Generado: sobre lo ya entregado, en su fecha de entrega. Pendiente: sobre lo que falta por entregar."
      >
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-600">
              <tr>
                <th className="px-2 py-1.5 text-left" rowSpan={2}>SKU</th>
                <th className="px-2 py-1.5 text-left" rowSpan={2}>Gama / Cat / Sub</th>
                <th className="px-2 py-1.5 text-right" rowSpan={2}>Importe €</th>
                <th className="px-2 py-1.5 text-right" rowSpan={2} title="Descuento técnico en factura (se resta antes del rappel y la comisión)">Dto. técnico</th>
                <th className="px-2 py-1.5 text-right" rowSpan={2} title="Neto del descuento técnico">Entregado €</th>
                <th className="border-l border-slate-200 px-2 py-1 text-center" colSpan={3}>Rappel</th>
                <th className="border-l border-slate-200 px-2 py-1 text-center" colSpan={3}>Comisión PM</th>
                <th className="border-l border-slate-200 px-2 py-1 text-center" colSpan={3}>Provisión</th>
              </tr>
              <tr>
                {[0, 1, 2].map((i) => (
                  <Fragment key={i}>
                    <th className="border-l border-slate-200 px-2 py-1 text-right">%</th>
                    <th className="px-2 py-1 text-right">Generado</th>
                    <th className="px-2 py-1 text-right">Pendiente</th>
                  </Fragment>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filas.map((f) => (
                <tr key={f.linea.id}>
                  <td className="px-2 py-1.5 font-mono text-xs">{f.linea.sku_cliente ?? f.linea.producto_sku ?? '—'}</td>
                  <td className="px-2 py-1.5 text-xs">
                    {f.cond
                      ? <>{f.linea.producto_gama_nombre ?? '—'} / {f.linea.producto_categoria_nombre ?? '—'} / {f.linea.producto_subcategoria_nombre ?? '—'}</>
                      : <span className="text-amber-700" title="Ninguna condición comercial del cliente aplica a este producto (revisa la marca y la taxonomía).">Sin condición comercial</span>}
                  </td>
                  <td className="px-2 py-1.5 text-right font-mono text-xs">{fmt.eur(f.subtotal)}</td>
                  <td className="px-2 py-1.5 text-right font-mono text-xs text-slate-500" title={f.linea.dto_tecnico_motivo ? MOTIVO_SIN_DTO[f.linea.dto_tecnico_motivo] : undefined}>
                    {f.dtoPct ? <>−{fmt.eur(f.dto)} <span className="text-[10px]">({fmt.pct(f.dtoPct)})</span></> : f.linea.dto_tecnico_motivo ? <span className="text-[10px]">excluido</span> : '—'}
                  </td>
                  <td className="px-2 py-1.5 text-right font-mono text-xs">{fmt.eur(f.baseGenerada)}</td>
                  <Celda v={f.rappel} />
                  <Celda v={f.comision} />
                  <Celda v={f.provision} />
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-slate-300 bg-slate-50 font-semibold">
                <td className="px-2 py-1.5 text-xs text-slate-600" colSpan={2}>TOTALES</td>
                <td className="px-2 py-1.5 text-right font-mono text-xs">{fmt.eur(suma((f) => f.subtotal))}</td>
                <td className="px-2 py-1.5 text-right font-mono text-xs text-slate-500">{suma((f) => f.dto) ? `−${fmt.eur(suma((f) => f.dto))}` : '—'}</td>
                <td className="px-2 py-1.5 text-right font-mono text-xs">{fmt.eur(suma((f) => f.baseGenerada))}</td>
                {totales.map((t) => (
                  <Fragment key={t.k}>
                    <td></td>
                    <td className="px-2 py-1.5 text-right font-mono text-xs text-emerald-700">{fmt.eur(t.generado)}</td>
                    <td className="px-2 py-1.5 text-right font-mono text-xs text-slate-500">{fmt.eur(t.pendiente)}</td>
                  </Fragment>
                ))}
              </tr>
            </tfoot>
          </table>
        </div>
        <p className="mt-2 text-xs text-slate-500">
          La provisión sale del <strong>% financiero</strong> de las condiciones comerciales del cliente.
          {sinCondicion > 0 && <span className="text-amber-700"> {sinCondicion} línea(s) sin condición comercial no suman.</span>}
        </p>
      </Card>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Precio compra proveedor + CDP (heredados)
// ─────────────────────────────────────────────────────────────────────

// Identificador interno legible del pedido a proveedor: PP-000045.
const ppCodigo = (n: number | null | undefined): string =>
  n == null ? '—' : `PP-${String(n).padStart(6, '0')}`;

function CompraCdpCard({ lineas }: { lineas: LineaRow[] }) {
  const navigate = useNavigate();
  return (
    <Card title="Precio compra proveedor & CDP (heredado)" description="Datos heredados del pedido a proveedor vinculado y de la cotización que dio origen a la línea. El Nº pedido APPROX a proveedor y el Nº PI proveedor se editan en cada pedido a proveedor.">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-600">
            <tr>
              <th className="px-2 py-1.5 text-left">SKU</th>
              <th className="px-2 py-1.5 text-left">Cotización</th>
              <th className="px-2 py-1.5 text-left">ID pedido proveedor</th>
              <th className="px-2 py-1.5 text-left">Nº pedido APPROX a prov.</th>
              <th className="px-2 py-1.5 text-left">Nº PI proveedor</th>
              <th className="px-2 py-1.5 text-right">Precio compra USD</th>
              <th className="px-2 py-1.5 text-right">CDPe €</th>
              <th className="px-2 py-1.5 text-right">Coste final €</th>
              <th className="px-2 py-1.5 text-right">PVPr c/IVA €</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {lineas.map((l) => (
              <tr key={l.id}>
                <td className="px-2 py-1.5 font-mono text-xs">{l.sku_cliente ?? l.producto_sku ?? '—'}</td>
                <td className="px-2 py-1.5 text-xs">
                  {l.cotizacion_id
                    ? <span className="font-mono">{l.cotizacion_id.slice(0, 8)}</span>
                    : <span className="text-slate-300">—</span>}
                </td>
                <td className="px-2 py-1.5 text-xs">
                  {l.pedido_proveedor_id
                    ? (
                      <button
                        type="button"
                        onClick={() => navigate(`/proveedores/pedidos?open=${l.pedido_proveedor_id}`)}
                        className="cursor-pointer font-mono font-semibold text-sky-600 hover:underline"
                        title={`Abrir pedido a proveedor (${l.pedido_proveedor_estado ?? '—'})`}
                      >
                        {ppCodigo(l.pedido_proveedor_numero_seq)}
                      </button>
                    )
                    : <span className="text-slate-300">—</span>}
                </td>
                <td className="px-2 py-1.5 text-xs font-mono">{l.pedido_proveedor_numero_approx || <span className="text-slate-300">—</span>}</td>
                <td className="px-2 py-1.5 text-xs font-mono">{l.pedido_proveedor_pi || <span className="text-slate-300">—</span>}</td>
                <td className="px-2 py-1.5 text-right font-mono text-xs">{fmt.usd(l.precio_compra_usd)}</td>
                <td className="px-2 py-1.5 text-right font-mono text-xs">{fmt.eur(l.cotizacion_cdpe_eur)}</td>
                <td className="px-2 py-1.5 text-right font-mono text-xs">{fmt.eur(l.cotizacion_coste_game_final_eur)}</td>
                <td className="px-2 py-1.5 text-right font-mono text-xs">{fmt.eur(l.cotizacion_pvpr_iva_eur)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Log de modificaciones
// ─────────────────────────────────────────────────────────────────────

const CAMPO_LOG: Record<string, string> = {
  estado: 'Estado', unidades: 'Unidades', unidades_entregadas: 'Uds. entregadas', precio_unitario: 'Precio',
  fecha_pedido: 'Fecha pedido', fecha_pedido_confirmado: 'Confirmado', fecha_entrega: 'Fecha de entrega',
  fecha_entrega_solicitada: 'Entrega solicitada', fecha_entrega_real: 'Entrega real', etd: 'ETD', eta: 'ETA',
  fecha_estimada_llegada: 'ETA proveedor', fecha_recepcion: 'Recepción', numero_approx: 'Nº APPROX',
  numero_cliente: 'Nº cliente', pi_proveedor: 'PI proveedor', notas: 'Notas', motivo: 'Motivo',
  motivo_cancelacion: 'Motivo cancelación', cancelada: 'Cancelada', sku_cliente: 'SKU', descripcion: 'Descripción',
  pedido: 'Pedido', fusionado_con: '🔗 Fusionado con', fusionado_en: '🔗 Fusionado en',
};

const valorLog = (v: unknown): string => {
  if (v == null || v === '') return '∅';
  if (typeof v === 'boolean') return v ? 'sí' : 'no';
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}/.test(v)) return v.length > 10 ? fmt.datetime(v) : fmt.date(v);
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
};

/** Resume el diff jsonb de una entrada del log en frases legibles. */
function resumenCambios(c: Record<string, unknown>): string[] {
  const out: string[] = [];
  for (const [k, v] of Object.entries(c ?? {})) {
    const o = v as Record<string, unknown> | null;
    if (k === 'entrega_registrada' && o) {
      out.push(`📦 Entrega registrada: ${fmt.int(Number(o.unidades))} uds${o.fecha_entrega && o.fecha_entrega !== 'CURRENT_DATE' ? ` el ${fmt.date(String(o.fecha_entrega))}` : ''}${Array.isArray(o.albaranes) && o.albaranes.length ? ` · albarán ${o.albaranes.join(', ')}` : ''}`);
    } else if (k === 'entrega_editada' && o) {
      out.push(`📦 Entrega corregida${o.unidades != null ? `: ${fmt.int(Number(o.unidades))} uds` : ''}${o.fecha_entrega ? ` · fecha ${fmt.date(String(o.fecha_entrega))}` : ''}`);
    } else if (k === 'entrega_eliminada') {
      out.push('📦 Entrega eliminada');
    } else if (o && typeof o === 'object' && !Array.isArray(o) && ('antes' in o || 'despues' in o)) {
      out.push(`${CAMPO_LOG[k] ?? k}: ${valorLog(o.antes)} → ${valorLog(o.despues)}`);
    } else if (k !== 'id' && !k.startsWith('__')) {
      out.push(`${CAMPO_LOG[k] ?? k}: ${valorLog(v)}`);
    }
  }
  return out;
}

const QUE_LOG: Record<ModificacionRow['target_table'], string> = {
  pedidos_cliente: 'Pedido', pedidos_cliente_lineas: 'Línea', pedido_cliente_archivos: 'Documento', pedidos: 'Pedido a proveedor',
};
const ACCION_LOG: Record<ModificacionRow['accion'], { txt: string; color: string }> = {
  insert: { txt: 'alta', color: '#10b981' }, update: { txt: 'cambio', color: '#0891b2' }, delete: { txt: 'baja', color: '#dc2626' },
};

function ModificacionesCard({
  pedido,
  modificaciones,
  cargando,
}: {
  pedido: PedidoClienteFull;
  modificaciones: ModificacionRow[];
  cargando: boolean;
}) {
  const qc = useQueryClient();
  const [editId, setEditId] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [verJson, setVerJson] = useState<Set<string>>(new Set());
  const [filtro, setFiltro] = useState<'todo' | 'cliente' | 'entregas' | 'proveedor'>('todo');
  const skuLinea = useMemo(() => new Map(pedido.lineas.map((l) => [l.id, l.sku_cliente ?? l.producto_sku ?? l.id.slice(0, 8)])), [pedido.lineas]);

  const guardar = useMutation({
    mutationFn: (modId: string) => api.patch(`/pedidos-cliente/${pedido.id}/modificaciones/${modId}`, { observaciones: draft }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pedido-cliente', pedido.id, 'modificaciones'] });
      setEditId(null);
      setDraft('');
    },
  });

  const esEntrega = (m: ModificacionRow) => Object.keys(m.cambios ?? {}).some((k) => k.startsWith('entrega_'));
  const visibles = modificaciones.filter((m) => filtro === 'todo'
    || (filtro === 'proveedor' ? m.origen === 'proveedor' : filtro === 'entregas' ? esEntrega(m) : m.origen === 'cliente'));

  return (
    <Card
      title={`Log de modificaciones (${modificaciones.length})`}
      description="Cada cambio del pedido, de sus líneas, entregas y documentos, y de los pedidos a proveedor enlazados, con fecha, usuario y antes → después. Puedes añadir una nota a las entradas del pedido."
      actions={
        <Select
          value={filtro}
          emptyLabel={null}
          onChange={(e) => setFiltro(e.target.value as typeof filtro)}
          options={[
            { value: 'todo', label: 'Todo' },
            { value: 'cliente', label: 'Solo pedido cliente' },
            { value: 'entregas', label: 'Solo entregas' },
            { value: 'proveedor', label: 'Solo pedidos a proveedor' },
          ]}
        />
      }
    >
      {cargando ? <Spinner /> : visibles.length === 0 ? (
        <p className="text-sm italic text-slate-400">Sin modificaciones registradas.</p>
      ) : (
        <ul className="space-y-2">
          {visibles.map((m) => {
            const lineas = resumenCambios(m.cambios);
            const sobre = m.target_table === 'pedidos'
              ? ppCodigo(m.pedido_proveedor_numero_seq)
              : m.target_table === 'pedidos_cliente_lineas' ? skuLinea.get(m.target_id) ?? null : null;
            return (
              <li key={m.id} className={`rounded border p-3 ${m.origen === 'proveedor' ? 'border-violet-200 bg-violet-50/40' : 'border-slate-200 bg-white'}`}>
                <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500">
                  <span className="flex flex-wrap items-center gap-1.5">
                    <span className="font-mono">{fmt.datetime(m.fecha_hora)}</span>
                    <Badge color={ACCION_LOG[m.accion]?.color ?? '#64748b'}>{ACCION_LOG[m.accion]?.txt ?? m.accion}</Badge>
                    <span className="font-medium text-slate-700">{QUE_LOG[m.target_table] ?? m.target_table}</span>
                    {sobre && <span className="font-mono text-slate-700">{sobre}</span>}
                    <span>· {m.usuario_email ?? 'sistema'}</span>
                  </span>
                  <span className="flex gap-3">
                    <button
                      type="button"
                      className="cursor-pointer text-xs text-slate-400 hover:text-slate-700"
                      onClick={() => setVerJson((s) => { const n = new Set(s); if (n.has(m.id)) n.delete(m.id); else n.add(m.id); return n; })}
                    >
                      {verJson.has(m.id) ? 'ocultar detalle' : 'ver detalle'}
                    </button>
                    {m.origen === 'cliente' && (
                      <button
                        type="button"
                        className="cursor-pointer text-xs text-slate-500 hover:text-slate-700"
                        onClick={() => { setEditId(m.id); setDraft(m.observaciones ?? ''); }}
                      >
                        {m.observaciones ? '✏️ Editar nota' : '+ Añadir nota'}
                      </button>
                    )}
                  </span>
                </div>
                {lineas.length > 0 && (
                  <ul className="mt-1.5 space-y-0.5 text-xs text-slate-800">
                    {lineas.map((t, i) => <li key={i}>{t}</li>)}
                  </ul>
                )}
                {verJson.has(m.id) && (
                  <pre className="mt-2 max-h-48 overflow-y-auto rounded bg-slate-50 p-2 text-[11px] text-slate-700">{JSON.stringify(m.cambios, null, 2)}</pre>
                )}
                {editId === m.id ? (
                  <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-end">
                    <Textarea
                      rows={2}
                      value={draft}
                      onChange={(e) => setDraft(e.target.value)}
                      placeholder="Observación manual…"
                      className="flex-1"
                    />
                    <div className="flex gap-2">
                      <Button size="sm" variant="danger" onClick={() => { setEditId(null); setDraft(''); }} disabled={guardar.isPending}>Cancelar</Button>
                      <Button size="sm" variant="save" onClick={() => guardar.mutate(m.id)} disabled={guardar.isPending}>
                        {guardar.isPending ? 'Guardando…' : 'Guardar nota'}
                      </Button>
                    </div>
                  </div>
                ) : m.observaciones ? (
                  <p className="mt-2 rounded bg-amber-50 px-2 py-1 text-xs text-amber-800">📝 {m.observaciones}</p>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}

function Row({ k, children }: { k: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <dt className="text-slate-500">{k}</dt>
      <dd className="text-slate-900 text-right">{children}</dd>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Tabla de líneas (modo lectura — al pulsar una fila abre modal Editar)
// ─────────────────────────────────────────────────────────────────────

const entregadaDelTodo = (l: LineaRow) => !l.cancelada && l.unidades > 0 && (l.unidades_entregadas ?? 0) >= l.unidades;

function LineasTable({ lineas, onOpenEdit, onEntregar, onCancelar }: {
  lineas: LineaRow[];
  onOpenEdit: (lineaId: string) => void;
  onEntregar?: (lineaId: string) => void;
  onCancelar?: (lineaId: string, cancelada: boolean, motivo?: string) => void;
}) {
  const navigate = useNavigate();
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-slate-200 text-sm">
        <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-600">
          <tr>
            <th className="px-2 py-1.5 text-left">SKU</th>
            <th className="px-2 py-1.5 text-left">P/N - Ref</th>
            <th className="px-2 py-1.5 text-left">Descripción</th>
            <th className="px-2 py-1.5 text-left">Gama</th>
            <th className="px-2 py-1.5 text-left">Cat. / Sub.</th>
            <th className="px-2 py-1.5 text-right">Uds.</th>
            <th className="px-2 py-1.5 text-right">Entregadas</th>
            <th className="px-2 py-1.5 text-left">Fecha entrega</th>
            <th className="px-2 py-1.5 text-right">Precio €</th>
            <th className="px-2 py-1.5 text-right">Subtotal</th>
            <th className="px-2 py-1.5 text-left">Entrega solic.</th>
            <th className="px-2 py-1.5 text-left">ETD</th>
            <th className="px-2 py-1.5 text-left">ETA</th>
            <th className="px-2 py-1.5 text-left">Entrega estim.</th>
            <th className="px-2 py-1.5 text-left">Cotización</th>
            <th className="px-2 py-1.5 text-left">Pedido a proveedor</th>
            {(onCancelar || onEntregar) && <th className="px-2 py-1.5 text-center" style={{ width: '4.5rem' }}></th>}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {lineas.map((l) => {
            const subtotal = (Number(l.precio_unitario) || 0) * Math.max(l.unidades, l.unidades_entregadas ?? 0);
            const isCancelled = Boolean(l.cancelada);
            const completa = entregadaDelTodo(l);
            const entregadas = l.unidades_entregadas ?? 0;
            return (
              <tr
                key={l.id}
                className={`align-top ${completa ? 'bg-emerald-50 hover:bg-emerald-100' : 'hover:bg-slate-50'} ${isCancelled ? 'opacity-40 line-through' : 'cursor-pointer'}`}
                onClick={() => !isCancelled && onOpenEdit(l.id)}
                title={isCancelled ? (l.motivo_cancelacion ? `Cancelada: ${l.motivo_cancelacion}` : 'Línea cancelada') : completa ? 'Entregada del todo · click para editar la línea' : 'Click para editar línea'}
              >
                <td className="px-2 py-1.5 font-mono text-xs">{l.sku_cliente ?? l.producto_sku ?? '—'}</td>
                <td className="px-2 py-1.5 font-mono text-xs">{l.pn_ref ?? '—'}</td>
                <td className="min-w-[180px] px-2 py-1.5 text-xs">{l.producto_nombre ?? l.descripcion ?? '—'}</td>
                <td className="px-2 py-1.5 text-xs">{l.producto_gama_nombre ?? <span className="text-slate-300">—</span>}</td>
                <td className="px-2 py-1.5 text-xs">
                  {l.producto_categoria_nombre ?? <span className="text-slate-300">—</span>}
                  {l.producto_subcategoria_nombre && <span className="text-slate-500"> / {l.producto_subcategoria_nombre}</span>}
                </td>
                <td className="px-2 py-1.5 text-right font-mono text-xs">{fmt.int(l.unidades)}</td>
                <td className="px-2 py-1.5 text-right font-mono text-xs">
                  {entregadas === 0
                    ? <span className="text-slate-300">0</span>
                    : (
                      <span
                        className={completa ? 'font-semibold text-emerald-700' : 'text-amber-700'}
                        title={entregadas > l.unidades ? `${fmt.int(entregadas - l.unidades)} de más (sobre-entrega)` : completa ? 'Entregada del todo' : `Faltan ${fmt.int(l.unidades - entregadas)}`}
                      >
                        {fmt.int(entregadas)}{entregadas > l.unidades && <span className="ml-0.5 text-amber-600">*</span>}
                      </span>
                    )}
                  {!completa && entregadas > 0 && <div className="text-[10px] text-slate-400">faltan {fmt.int(l.unidades - entregadas)}</div>}
                </td>
                <td className="px-2 py-1.5 text-xs">{l.ultima_entrega_fecha ? fmt.date(l.ultima_entrega_fecha) : <span className="text-slate-300">—</span>}</td>
                <td className="px-2 py-1.5 text-right font-mono text-xs">{l.precio_unitario != null ? fmt.eur(l.precio_unitario) : '—'}</td>
                <td className="px-2 py-1.5 text-right font-mono text-xs font-semibold">{fmt.eur(subtotal)}</td>
                <td className="px-2 py-1.5 text-xs">{fmt.date(l.fecha_entrega_solicitada) ?? '—'}</td>
                {/* ETD/ETA sincronizados con el pedido a proveedor: el ETD lo
                    escribe el pedido a proveedor en la línea; el ETA sale de la
                    fecha estimada de llegada del pedido a proveedor. */}
                <td className="px-2 py-1.5 text-xs">{fmt.date(l.etd) ?? '—'}</td>
                <td className="px-2 py-1.5 text-xs">{fmt.date(l.pedido_proveedor_eta ?? l.eta) ?? '—'}</td>
                <td className="px-2 py-1.5 text-xs">{fmt.date(l.fecha_entrega_estimada) ?? '—'}</td>
                <td className="px-2 py-1.5 text-xs">
                  {l.cotizacion_id
                    ? <span className="font-mono text-[11px]">{l.cotizacion_id.slice(0, 8)}</span>
                    : <span className="text-slate-300">—</span>}
                </td>
                <td className="px-2 py-1.5 text-xs" onClick={(e) => e.stopPropagation()}>
                  {l.pedido_proveedor_id
                    ? (
                      <button
                        type="button"
                        onClick={() => navigate(`/proveedores/pedidos?open=${l.pedido_proveedor_id}`)}
                        className="cursor-pointer font-mono text-[11px] font-semibold text-sky-600 hover:underline"
                        title={`Abrir el pedido a proveedor (${l.pedido_proveedor_estado ?? '—'})`}
                      >
                        {ppCodigo(l.pedido_proveedor_numero_seq)}
                      </button>
                    )
                    : <span className="italic text-slate-400">—</span>}
                </td>
                {(onCancelar || onEntregar) && (
                  <td className="whitespace-nowrap px-2 py-1.5 text-center" onClick={(e) => e.stopPropagation()}>
                    {onEntregar && !isCancelled && !completa && (
                      <button
                        type="button"
                        onClick={() => onEntregar(l.id)}
                        className="mr-1 cursor-pointer rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700 hover:bg-emerald-100"
                        title="Registrar una entrega de esta línea"
                      >📦</button>
                    )}
                    {onCancelar && (isCancelled ? (
                      <button
                        type="button"
                        onClick={() => onCancelar(l.id, false)}
                        className="cursor-pointer rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700 hover:bg-emerald-100"
                        title="Reactivar línea"
                      >↩</button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => {
                          const motivo = prompt('Motivo de cancelación (opcional):');
                          if (motivo === null) return;
                          onCancelar(l.id, true, motivo || undefined);
                        }}
                        className="cursor-pointer rounded bg-rose-50 px-1.5 py-0.5 text-[10px] font-medium text-rose-700 hover:bg-rose-100"
                        title="Cancelar esta línea"
                      >✕</button>
                    ))}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Entregas: historial de todas las entregas del pedido + registrar una
// ─────────────────────────────────────────────────────────────────────

function EntregasCard({ pedido, onRegistrar }: { pedido: PedidoClienteFull; onRegistrar: () => void }) {
  const qc = useQueryClient();
  const activas = pedido.lineas.filter((l) => !l.cancelada);
  const previstas = activas.reduce((a, l) => a + l.unidades, 0);
  const entregadas = activas.reduce((a, l) => a + Math.min(l.unidades_entregadas ?? 0, l.unidades), 0);
  const completas = activas.filter(entregadaDelTodo).length;
  const todo = activas.length > 0 && completas === activas.length;
  const borrar = useMutation({
    mutationFn: (e: EntregaRow) => api.delete(`/pedidos-cliente/${pedido.id}/lineas/${e.linea_id}/entregas/${e.id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pedido-cliente', pedido.id] });
      qc.invalidateQueries({ queryKey: ['pedidos-cliente'] });
      toast.success('Entrega eliminada', 'Queda anotado en el log de modificaciones.');
    },
    onError: (err) => toast.error('No se pudo eliminar la entrega', (err as Error).message),
  });

  return (
    <Card
      title={`Historial de entregas (${pedido.entregas.length})`}
      description={todo
        ? `Pedido entregado del todo: ${fmt.int(entregadas)} de ${fmt.int(previstas)} uds.`
        : `${fmt.int(entregadas)} de ${fmt.int(previstas)} uds entregadas · ${completas} de ${activas.length} línea(s) completas.`}
      actions={
        activas.some((l) => !entregadaDelTodo(l))
          ? <Button variant="create" size="sm" onClick={onRegistrar}>📦 Registrar entrega</Button>
          : undefined
      }
    >
      {previstas > 0 && (
        <div className="mb-3 h-2 overflow-hidden rounded-full bg-slate-100" title={`${Math.round((entregadas / previstas) * 100)} % entregado`}>
          <div className="h-full rounded-full bg-emerald-500" style={{ width: `${Math.min(100, (entregadas / previstas) * 100)}%` }} />
        </div>
      )}
      {pedido.entregas.length === 0 ? (
        <p className="text-sm italic text-slate-400">
          {pedido.fecha_entrega
            ? `Sin entregas parciales registradas. El pedido se marcó entregado el ${fmt.date(pedido.fecha_entrega)}.`
            : 'Aún no hay entregas registradas.'}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-600">
              <tr>
                <th className="px-2 py-1.5 text-left">Fecha entrega</th>
                <th className="px-2 py-1.5 text-left">SKU</th>
                <th className="px-2 py-1.5 text-left">Descripción</th>
                <th className="px-2 py-1.5 text-right">Uds.</th>
                <th className="px-2 py-1.5 text-left">Notas / albarán</th>
                <th className="px-2 py-1.5 text-left">Registrada</th>
                <th className="px-2 py-1.5"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {pedido.entregas.map((e) => (
                <tr key={e.id}>
                  <td className="px-2 py-1.5 text-xs font-medium">{fmt.date(e.fecha_entrega)}</td>
                  <td className="px-2 py-1.5 font-mono text-xs">{e.sku ?? '—'}</td>
                  <td className="px-2 py-1.5 text-xs">{e.descripcion ?? '—'}</td>
                  <td className="px-2 py-1.5 text-right font-mono text-xs font-semibold">{fmt.int(e.unidades)}</td>
                  <td className="px-2 py-1.5 text-xs text-slate-600">{e.notas ?? <span className="text-slate-300">—</span>}</td>
                  <td className="px-2 py-1.5 text-xs text-slate-500">{fmt.datetime(e.created_at)}{e.created_by_email ? ` · ${e.created_by_email}` : ''}</td>
                  <td className="px-2 py-1.5 text-right">
                    <button
                      type="button"
                      className="cursor-pointer text-xs text-rose-600 hover:text-rose-800"
                      title="Eliminar esta entrega (queda en el log)"
                      disabled={borrar.isPending}
                      onClick={async () => {
                        if (await confirmar({ title: '¿Eliminar esta entrega?', message: `${fmt.int(e.unidades)} uds de ${e.sku ?? 'la línea'} del ${fmt.date(e.fecha_entrega)}. Se recalculan las unidades entregadas y el estado del pedido.`, confirmLabel: 'Eliminar', danger: true })) borrar.mutate(e);
                      }}
                    >×</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function RegistrarEntregaModal({ pedido, lineaInicial, onClose }: { pedido: PedidoClienteFull; lineaInicial: string | null; onClose: () => void }) {
  const qc = useQueryClient();
  const pendientes = pedido.lineas.filter((l) => !l.cancelada && !entregadaDelTodo(l));
  const [lineaId, setLineaId] = useState(lineaInicial ?? pendientes[0]?.id ?? '');
  const linea = pedido.lineas.find((l) => l.id === lineaId) ?? null;
  const faltan = linea ? Math.max(0, linea.unidades - (linea.unidades_entregadas ?? 0)) : 0;
  const [uds, setUds] = useState(String(faltan || ''));
  const [fecha, setFecha] = useState(new Date().toLocaleDateString('sv-SE'));
  const [notas, setNotas] = useState('');

  const entregar = useMutation({
    mutationFn: (confirmarSobre: boolean) => api.post<{ unidades_pendientes: number; estado_pedido: { estado_nuevo: string } | null }>(
      `/pedidos-cliente/${pedido.id}/lineas/${lineaId}/entregar`,
      { unidades: Number(uds), fecha_entrega: fecha || undefined, notas: notas.trim() || undefined, confirmar_sobreentrega: confirmarSobre },
    ),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['pedido-cliente', pedido.id] });
      qc.invalidateQueries({ queryKey: ['pedidos-cliente'] });
      qc.invalidateQueries({ queryKey: ['pedidos-cliente-lineas'] });
      const estado = r.estado_pedido?.estado_nuevo;
      toast.success('Entrega registrada', estado === 'FINALIZADO'
        ? 'Todas las líneas entregadas: el pedido pasa a FINALIZADO.'
        : r.unidades_pendientes > 0 ? `Quedan ${fmt.int(r.unidades_pendientes)} uds pendientes en esta línea.` : 'Línea entregada del todo.');
      onClose();
    },
    onError: async (err) => {
      const msg = (err as Error).message;
      const m = msg.match(/→\s*\d+:\s*(.+)$/);
      if (/sobre_entrega|409/i.test(msg)) {
        if (await confirmar({ title: '¿Registrar la sobre-entrega?', message: m ? m[1] : msg, confirmLabel: 'Registrar igualmente', danger: true })) entregar.mutate(true);
        return;
      }
      toast.error('No se pudo registrar la entrega', m ? m[1] : msg);
    },
  });

  const valido = !!linea && Number(uds) > 0 && !!fecha;
  return (
    <Modal
      open
      onClose={onClose}
      title="Registrar entrega"
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={entregar.isPending}>Cancelar</Button>
          <Button variant="save" onClick={() => entregar.mutate(false)} disabled={!valido || entregar.isPending}>
            {entregar.isPending ? 'Guardando…' : '📦 Registrar entrega'}
          </Button>
        </>
      }
    >
      <div className="space-y-3 text-sm">
        <FormRow label="Línea">
          <Select
            value={lineaId}
            emptyLabel={null}
            onChange={(e) => {
              setLineaId(e.target.value);
              const l = pedido.lineas.find((x) => x.id === e.target.value);
              setUds(l ? String(Math.max(0, l.unidades - (l.unidades_entregadas ?? 0)) || '') : '');
            }}
            options={pendientes.map((l) => ({
              value: l.id,
              label: `${l.sku_cliente ?? l.producto_sku ?? '—'} · ${l.producto_nombre ?? l.descripcion ?? ''} (faltan ${fmt.int(Math.max(0, l.unidades - (l.unidades_entregadas ?? 0)))})`,
            }))}
          />
        </FormRow>
        {linea && (
          <p className="text-xs text-slate-500">
            Pedidas {fmt.int(linea.unidades)} · entregadas {fmt.int(linea.unidades_entregadas ?? 0)} · faltan {fmt.int(faltan)}
          </p>
        )}
        <div className="grid gap-3 sm:grid-cols-2">
          <FormRow label="Unidades entregadas" required>
            <Input type="number" min={1} value={uds} onChange={(e) => setUds(e.target.value)} />
          </FormRow>
          <FormRow label="Fecha de entrega" required>
            <Input type="date" value={fecha} max={new Date().toLocaleDateString('sv-SE')} onChange={(e) => setFecha(e.target.value)} />
          </FormRow>
        </div>
        <FormRow label="Notas" hint="Nº de albarán, transportista, incidencias…">
          <Input value={notas} onChange={(e) => setNotas(e.target.value)} />
        </FormRow>
      </div>
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Pedidos a proveedor enlazados a las líneas del pedido
// ─────────────────────────────────────────────────────────────────────

function useGenerarPedidosProveedor(pedidoId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ creados: number; ya_existentes: number; sin_producto: number; sin_coste: number; sin_coste_lineas: string[]; productos_creados?: { creados: number; enlazados: number; enriquecidos: number; sin_marca: number } }>(`/pedidos-cliente/${pedidoId}/generar-pedidos-proveedor`, {}),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['pedido-cliente', pedidoId] });
      const pc = r.productos_creados;
      const partes = [`${r.creados} creado(s)`];
      if (pc?.creados) partes.push(`${pc.creados} producto(s) dados de alta`);
      if (pc?.enriquecidos) partes.push(`${pc.enriquecidos} producto(s) completados con la taxonomía`);
      if (pc?.enlazados) partes.push(`${pc.enlazados} enlazado(s) a producto existente`);
      if (r.ya_existentes) partes.push(`${r.ya_existentes} ya existían`);
      if (r.sin_producto) partes.push(`${r.sin_producto} sin producto (no generados)`);
      if (pc?.sin_marca) partes.push(`${pc.sin_marca} sin marca en la cotización (revisa la cotización)`);
      const aviso = avisoSinCoste(r);
      (aviso ? toast.warning : toast.success)('Pedidos a proveedor generados', `${partes.join(', ')}.${aviso}`);
    },
    onError: (err) => toast.error('No se pudieron generar los pedidos a proveedor', (err as Error).message),
  });
}

function PedidosProveedorCard({ pedido }: { pedido: PedidoClienteFull }) {
  const navigate = useNavigate();
  const generar = useGenerarPedidosProveedor(pedido.id);
  const [abiertos, setAbiertos] = useState<Set<string>>(new Set());
  const lineaDe = useMemo(() => new Map(pedido.lineas.map((l) => [l.id, l])), [pedido.lineas]);
  const sinPedido = pedido.lineas.filter((l) => !l.cancelada && !l.pedido_proveedor_id);
  const confirmado = (ESTADOS_CONFIRMADOS as readonly string[]).includes(pedido.estado);
  const toggle = (id: string) => setAbiertos((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  return (
    <Card
      title={`Pedidos a proveedor (${pedido.pedidos_proveedor.length})`}
      description="Un pedido a proveedor por línea del pedido de cliente. Pulsa una fila para ver el detalle; el código abre la ficha completa en Pedidos a proveedor."
      actions={sinPedido.length > 0 && confirmado
        ? (
          <Button variant="create" size="sm" onClick={() => generar.mutate()} disabled={generar.isPending}
            title="Crea el pedido a proveedor de las líneas que aún no lo tienen.">
            {generar.isPending ? 'Generando…' : `⊕ Generar los que faltan (${sinPedido.length})`}
          </Button>
        )
        : undefined}
    >
      {pedido.pedidos_proveedor.length === 0 ? (
        <p className="text-sm italic text-slate-400">
          {confirmado
            ? 'Este pedido no tiene pedidos a proveedor enlazados.'
            : 'Aún no hay pedidos a proveedor: se generan al pasar el pedido a Aceptado (o línea a línea al aceptar cada producto).'}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-600">
              <tr>
                <th className="w-6 px-2 py-1.5"></th>
                <th className="px-2 py-1.5 text-left">Pedido</th>
                <th className="px-2 py-1.5 text-left">Proveedor</th>
                <th className="px-2 py-1.5 text-left">Producto</th>
                <th className="px-2 py-1.5 text-right">Uds.</th>
                <th className="px-2 py-1.5 text-right">Precio USD</th>
                <th className="px-2 py-1.5 text-right">Importe USD</th>
                <th className="px-2 py-1.5 text-left">Fecha</th>
                <th className="px-2 py-1.5 text-left">ETA</th>
                <th className="px-2 py-1.5 text-left">Recepción</th>
                <th className="px-2 py-1.5 text-left">Estado</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {pedido.pedidos_proveedor.map((pp) => {
                const abierto = abiertos.has(pp.id);
                const l = lineaDe.get(pp.linea_id);
                return (
                  <Fragment key={pp.id}>
                    <tr className={`cursor-pointer align-top hover:bg-slate-50 ${abierto ? 'bg-slate-50' : ''}`} onClick={() => toggle(pp.id)}>
                      <td className="px-2 py-1.5 font-mono text-xs text-slate-500">{abierto ? '−' : '+'}</td>
                      <td className="px-2 py-1.5 text-xs" onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          onClick={() => navigate(`/proveedores/pedidos?open=${pp.id}`)}
                          className="cursor-pointer font-mono font-semibold text-sky-600 hover:underline"
                          title="Abrir la ficha del pedido a proveedor"
                        >
                          {ppCodigo(pp.numero_seq)}
                        </button>
                        {pp.numero_approx && <div className="font-mono text-[10px] text-slate-500">{pp.numero_approx}</div>}
                      </td>
                      <td className="px-2 py-1.5 text-xs">{pp.proveedor_nombre ?? <span className="text-amber-700">Sin proveedor</span>}</td>
                      <td className="px-2 py-1.5 text-xs">
                        <span className="font-mono">{pp.producto_sku ?? '—'}</span>
                        {pp.producto_nombre && <div className="text-slate-500">{pp.producto_nombre}</div>}
                      </td>
                      <td className="px-2 py-1.5 text-right font-mono text-xs">{fmt.int(pp.unidades)}</td>
                      <td className="px-2 py-1.5 text-right font-mono text-xs">{fmt.usd(pp.precio_compra_usd)}</td>
                      <td className="px-2 py-1.5 text-right font-mono text-xs font-semibold">{pp.precio_compra_usd != null ? fmt.usd(Number(pp.precio_compra_usd) * pp.unidades) : '—'}</td>
                      <td className="px-2 py-1.5 text-xs">{fmt.date(pp.fecha)}</td>
                      <td className="px-2 py-1.5 text-xs">{fmt.date(pp.fecha_estimada_llegada)}</td>
                      <td className="px-2 py-1.5 text-xs">{fmt.date(pp.fecha_recepcion)}</td>
                      <td className="px-2 py-1.5 text-xs"><Badge color={pp.estado_color ?? '#64748b'}>{pp.estado_nombre ?? pp.estado}</Badge></td>
                    </tr>
                    {abierto && (
                      <tr className="bg-slate-50">
                        <td></td>
                        <td colSpan={10} className="px-2 pb-3 pt-1">
                          <div className="grid gap-4 rounded-md border border-slate-200 bg-white p-3 text-xs sm:grid-cols-3">
                            <dl className="space-y-1">
                              <Row k="Nº APPROX a proveedor"><span className="font-mono">{pp.numero_approx || '—'}</span></Row>
                              <Row k="PI proveedor"><span className="font-mono">{pp.pi_proveedor || '—'}</span></Row>
                              <Row k="Ref. externa"><span className="font-mono">{pp.ref_externa || '—'}</span></Row>
                              <Row k="Documentos">{pp.archivos_count > 0 ? `📎 ${pp.archivos_count}` : '—'}</Row>
                            </dl>
                            <dl className="space-y-1">
                              <Row k="Alta">{fmt.datetime(pp.created_at)}</Row>
                              <Row k="Último cambio">{fmt.datetime(pp.updated_at)}</Row>
                              <Row k="Email al proveedor">{pp.email_generado_at ? fmt.datetime(pp.email_generado_at) : 'no generado'}</Row>
                              <Row k="Cambios en su log">{fmt.int(pp.modificaciones_count)}</Row>
                            </dl>
                            <dl className="space-y-1">
                              <Row k="Línea del cliente"><span className="font-mono">{l?.sku_cliente ?? l?.producto_sku ?? '—'}</span></Row>
                              <Row k="Uds. pedidas por el cliente">{l ? fmt.int(l.unidades) : '—'}</Row>
                              <Row k="Uds. entregadas al cliente">{l ? fmt.int(l.unidades_entregadas ?? 0) : '—'}</Row>
                              <Row k="Entrega solicitada">{fmt.date(l?.fecha_entrega_solicitada)}</Row>
                            </dl>
                            {(pp.notas || pp.incidencias) && (
                              <div className="space-y-1 sm:col-span-3">
                                {pp.notas && <p className="whitespace-pre-line text-slate-700"><span className="font-semibold">Notas:</span> {pp.notas}</p>}
                                {pp.incidencias && <p className="whitespace-pre-line rounded bg-rose-50 px-2 py-1 text-rose-800"><span className="font-semibold">Incidencias:</span> {pp.incidencias}</p>}
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {sinPedido.length > 0 && pedido.pedidos_proveedor.length > 0 && (
        <p className="mt-2 text-xs text-amber-700">
          {sinPedido.length} línea(s) sin pedido a proveedor: {sinPedido.map((l) => l.sku_cliente ?? l.producto_sku ?? '—').join(', ')}.
        </p>
      )}
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Editar cabecera (incluye estado + side-effect al pasar a ACEPTADO)
// ─────────────────────────────────────────────────────────────────────

function EditCabeceraModal({ pedido, onClose }: { pedido: PedidoClienteFull; onClose: () => void }) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState({
    fecha_pedido: pedido.fecha_pedido,
    fecha_pedido_confirmado: pedido.fecha_pedido_confirmado ?? '',
    numero_approx: pedido.numero_approx ?? '',
    numero_cliente: pedido.numero_cliente ?? '',
    pi_proveedor: pedido.pi_proveedor ?? '',
    notas: pedido.notas ?? '',
    etd: pedido.etd ?? '',
    eta: pedido.eta ?? '',
    fecha_entrega_estimada: pedido.fecha_entrega_estimada ?? '',
    estado: pedido.estado,
    // Se pide al pasar a ENTREGADO (hoy en hora local por defecto).
    fecha_entrega: new Date().toLocaleDateString('sv-SE'),
  });

  const guardar = useMutation({
    mutationFn: async () => {
      // 1) Guardar campos base
      // ETD / ETA / entrega estimada se aplican en el servidor a las líneas que
      // seguían a la cabecera (las ajustadas a mano no se tocan).
      const cab = await api.put<{ lineas_actualizadas?: number }>(`/pedidos-cliente/${pedido.id}`, {
        fecha_pedido: draft.fecha_pedido,
        fecha_pedido_confirmado: draft.fecha_pedido_confirmado || null,
        numero_approx: draft.numero_approx || null,
        numero_cliente: draft.numero_cliente || null,
        pi_proveedor: draft.pi_proveedor || null,
        notas: draft.notas || null,
        etd: draft.etd || null,
        eta: draft.eta || null,
        fecha_entrega_estimada: draft.fecha_entrega_estimada || null,
      });
      if (cab.lineas_actualizadas) toast.info('Fechas aplicadas a las líneas', `${cab.lineas_actualizadas} cambio(s) en líneas que seguían a la cabecera.`);
      // 2) Si cambia el estado, llamar al endpoint específico (puede crear
      //    pedidos a proveedor si pasa a ACEPTADO).
      let pedidosProveedor: { creados: number; ya_existentes: number; sin_producto: number; sin_coste: number; sin_coste_lineas: string[] } | null = null;
      if (draft.estado !== pedido.estado) {
        try {
          const res = await api.patch<{ pedidos_proveedor?: { creados: number; ya_existentes: number; sin_producto: number; sin_coste: number; sin_coste_lineas: string[] } | null }>(
            `/pedidos-cliente/${pedido.id}/estado`,
            { estado: draft.estado, ...(draft.estado === 'ENTREGADO' ? { fecha_entrega: draft.fecha_entrega } : {}) },
          );
          pedidosProveedor = res.pedidos_proveedor ?? null;
        } catch (err) {
          const msg = (err as Error).message;
          if (msg.includes('finalizar_con_pendientes') && (await confirmar({ title: '¿Forzar FINALIZADO igualmente?', message: msg.replace(/^.*:\s*/, ''), confirmLabel: 'Forzar FINALIZADO', danger: true }))) {
            await api.patch(`/pedidos-cliente/${pedido.id}/estado`, { estado: draft.estado, force: true });
          } else {
            throw err;
          }
        }
      }
      return { pedidosProveedor };
    },
    onSuccess: ({ pedidosProveedor }) => {
      qc.invalidateQueries({ queryKey: ['pedido-cliente', pedido.id] });
      qc.invalidateQueries({ queryKey: ['pedidos-cliente'] });
      if (pedidosProveedor) {
        const aviso = avisoSinCoste(pedidosProveedor);
        (aviso ? toast.warning : toast.success)('Pedidos a proveedor generados', `Creados: ${pedidosProveedor.creados}\nYa existían: ${pedidosProveedor.ya_existentes}\nSin producto: ${pedidosProveedor.sin_producto}${aviso}`);
      }
      onClose();
    },
    onError: (err) => {
      // El backend devuelve mensaje claro p.ej. "X línea(s) sin SKU…".
      // Sin onError la mutación se silenciaba y el CEO veía el pedido
      // "atascado" en el mismo estado.
      const msg = (err as Error).message;
      const m = msg.match(/→\s*\d+:\s*(.+)$/);
      toast.error('No se pudo guardar el pedido', m ? m[1] : msg);
    },
  });

  const handleSubmit = async () => {
    if (draft.estado !== pedido.estado && draft.estado === 'ENTREGADO' && !draft.fecha_entrega) {
      toast.error('Falta la fecha de entrega', 'Indica la fecha en que se entregó el pedido.');
      return;
    }
    if (draft.estado !== pedido.estado && draft.estado === 'ACEPTADO') {
      if (!(await confirmar({ title: '¿Pasar el pedido a ACEPTADO?', message: 'Pasar a ACEPTADO genera pedidos a proveedor para todas las líneas con producto asignado.', confirmLabel: 'Continuar' }))) return;
    }
    guardar.mutate();
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Editar cabecera del pedido"
      size="lg"
      footer={
        <>
          <Button variant="danger" onClick={onClose} disabled={guardar.isPending}>Cancelar</Button>
          <Button variant="save" onClick={handleSubmit} disabled={guardar.isPending}>
            {guardar.isPending ? 'Guardando…' : 'Guardar'}
          </Button>
        </>
      }
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <FormRow label="Estado">
          <Select
            options={ESTADOS}
            value={draft.estado}
            emptyLabel={null}
            onChange={(e) => setDraft((d) => ({ ...d, estado: e.target.value }))}
          />
        </FormRow>
        {draft.estado === 'ENTREGADO' && draft.estado !== pedido.estado && (
          <FormRow label="Fecha de entrega">
            <Input
              type="date"
              value={draft.fecha_entrega}
              max={new Date().toLocaleDateString('sv-SE')}
              onChange={(e) => setDraft((d) => ({ ...d, fecha_entrega: e.target.value }))}
              className="border-accent-400 ring-1 ring-accent-200"
            />
          </FormRow>
        )}
        <FormRow label="Fecha pedido"><Input type="date" value={draft.fecha_pedido} onChange={(e) => setDraft((d) => ({ ...d, fecha_pedido: e.target.value }))} /></FormRow>
        <FormRow label="Fecha confirmado"><Input type="date" value={draft.fecha_pedido_confirmado} onChange={(e) => setDraft((d) => ({ ...d, fecha_pedido_confirmado: e.target.value }))} /></FormRow>
        <FormRow label="Nº pedido APPROX"><Input value={draft.numero_approx} onChange={(e) => setDraft((d) => ({ ...d, numero_approx: e.target.value }))} /></FormRow>
        <FormRow label="Nº pedido CLIENTE"><Input value={draft.numero_cliente} onChange={(e) => setDraft((d) => ({ ...d, numero_cliente: e.target.value }))} /></FormRow>
        <FormRow label="PI Proveedor"><Input value={draft.pi_proveedor} onChange={(e) => setDraft((d) => ({ ...d, pi_proveedor: e.target.value }))} /></FormRow>
        <div className="grid gap-3 rounded-md border border-slate-200 bg-slate-50 p-3 sm:col-span-2 sm:grid-cols-3">
          <p className="text-xs text-slate-600 sm:col-span-3">Se aplican a todas las líneas del pedido. Las líneas que hayas ajustado a mano conservan su fecha.</p>
          <FormRow label="ETD"><Input type="date" value={draft.etd} onChange={(e) => setDraft((d) => ({ ...d, etd: e.target.value }))} /></FormRow>
          <FormRow label="ETA"><Input type="date" value={draft.eta} onChange={(e) => setDraft((d) => ({ ...d, eta: e.target.value }))} /></FormRow>
          <FormRow label="Entrega estimada"><Input type="date" value={draft.fecha_entrega_estimada} onChange={(e) => setDraft((d) => ({ ...d, fecha_entrega_estimada: e.target.value }))} /></FormRow>
        </div>
        <div className="sm:col-span-2">
          <FormRow label="Notas"><Textarea rows={2} value={draft.notas} onChange={(e) => setDraft((d) => ({ ...d, notas: e.target.value }))} /></FormRow>
        </div>
      </div>
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Editar línea (modal con Guardar/Cancelar + Borrar)
// ─────────────────────────────────────────────────────────────────────

function EditLineaModal({ pedidoClienteId, linea, onClose }: { pedidoClienteId: string; linea: LineaRow; onClose: () => void }) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState({
    sku_cliente: linea.sku_cliente ?? '',
    pn_ref: linea.pn_ref ?? '',
    descripcion: linea.descripcion ?? '',
    ean: linea.ean ?? '',
    unidades: String(linea.unidades),
    precio_unitario: linea.precio_unitario != null ? String(linea.precio_unitario) : '',
    etd: linea.etd ?? '',
    eta: linea.eta ?? '',
    fecha_entrega_solicitada: linea.fecha_entrega_solicitada ?? '',
    fecha_entrega_real: linea.fecha_entrega_real ?? '',
    fecha_entrega_estimada: linea.fecha_entrega_estimada ?? '',
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['pedido-cliente', pedidoClienteId] });

  const guardar = useMutation({
    mutationFn: () => api.patch(`/pedidos-cliente/${pedidoClienteId}/lineas/${linea.id}`, {
      sku_cliente: draft.sku_cliente || null,
      pn_ref: draft.pn_ref || null,
      descripcion: draft.descripcion || null,
      ean: draft.ean || null,
      unidades: Number(draft.unidades) || 1,
      precio_unitario: draft.precio_unitario === '' ? null : Number(draft.precio_unitario),
      etd: draft.etd || null,
      eta: draft.eta || null,
      fecha_entrega_solicitada: draft.fecha_entrega_solicitada || null,
      fecha_entrega_real: draft.fecha_entrega_real || null,
      fecha_entrega_estimada: draft.fecha_entrega_estimada || null,
    }),
    onSuccess: () => { invalidate(); onClose(); },
  });

  const borrar = useMutation({
    mutationFn: () => api.delete(`/pedidos-cliente/${pedidoClienteId}/lineas/${linea.id}`),
    onSuccess: () => { invalidate(); onClose(); },
  });

  return (
    <Modal
      open
      onClose={onClose}
      title="Editar línea del pedido"
      size="lg"
      footer={
        <>
          <Button
            variant="ghost"
            className="text-rose-600"
            onClick={async () => { if (await confirmar({ title: '¿Eliminar esta línea?', confirmLabel: 'Eliminar', danger: true })) borrar.mutate(); }}
            disabled={borrar.isPending || guardar.isPending}
          >
            {borrar.isPending ? 'Eliminando…' : 'Eliminar línea'}
          </Button>
          <div className="flex-1" />
          <Button variant="danger" onClick={onClose} disabled={guardar.isPending}>Cancelar</Button>
          <Button variant="save" onClick={() => guardar.mutate()} disabled={guardar.isPending}>
            {guardar.isPending ? 'Guardando…' : 'Guardar'}
          </Button>
        </>
      }
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <FormRow label="SKU cliente"><Input value={draft.sku_cliente} onChange={(e) => setDraft((d) => ({ ...d, sku_cliente: e.target.value }))} /></FormRow>
        <FormRow label="P/N - Ref"><Input value={draft.pn_ref} onChange={(e) => setDraft((d) => ({ ...d, pn_ref: e.target.value }))} /></FormRow>
        <div className="sm:col-span-2">
          <FormRow label="Descripción / nombre"><Input value={draft.descripcion} onChange={(e) => setDraft((d) => ({ ...d, descripcion: e.target.value }))} /></FormRow>
        </div>
        {linea.producto_id && (
          <div className="sm:col-span-2 rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-slate-600">
            Producto del catálogo vinculado: <span className="font-mono">{linea.producto_sku ?? '—'}</span>
            {linea.producto_nombre ? ` · ${linea.producto_nombre}` : ''}
            <div className="mt-1 text-slate-500">
              El listado muestra el nombre de la ficha del producto. Al guardar, el SKU cliente,
              la descripción y el EAN se copian a esa ficha si aún está pendiente de revisar.
            </div>
          </div>
        )}
        <FormRow label="EAN"><Input value={draft.ean} onChange={(e) => setDraft((d) => ({ ...d, ean: e.target.value }))} /></FormRow>
        <FormRow label="Unidades"><Input type="number" value={draft.unidades} onChange={(e) => setDraft((d) => ({ ...d, unidades: e.target.value }))} /></FormRow>
        <FormRow label="Precio unitario (EUR)"><Input type="number" step="any" value={draft.precio_unitario} onChange={(e) => setDraft((d) => ({ ...d, precio_unitario: e.target.value }))} /></FormRow>
        <p className="text-xs text-slate-500 sm:col-span-2">ETD, ETA y entrega estimada vienen de la cabecera del pedido; cambiarlas aquí ajusta solo esta línea.</p>
        <FormRow label="ETD"><Input type="date" value={draft.etd} onChange={(e) => setDraft((d) => ({ ...d, etd: e.target.value }))} /></FormRow>
        <FormRow label="ETA"><Input type="date" value={draft.eta} onChange={(e) => setDraft((d) => ({ ...d, eta: e.target.value }))} /></FormRow>
        <FormRow label="Entrega estimada"><Input type="date" value={draft.fecha_entrega_estimada} onChange={(e) => setDraft((d) => ({ ...d, fecha_entrega_estimada: e.target.value }))} /></FormRow>
        <FormRow label="Entrega solicitada"><Input type="date" value={draft.fecha_entrega_solicitada} onChange={(e) => setDraft((d) => ({ ...d, fecha_entrega_solicitada: e.target.value }))} /></FormRow>
        <FormRow label="Entrega real"><Input type="date" value={draft.fecha_entrega_real} onChange={(e) => setDraft((d) => ({ ...d, fecha_entrega_real: e.target.value }))} /></FormRow>
        {linea.pedido_proveedor_id && (
          <div className="sm:col-span-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
            Pedido a proveedor asociado: <span className="font-mono">{linea.pedido_proveedor_id.slice(0, 8)}</span> · estado <span className="font-semibold">{linea.pedido_proveedor_estado ?? '—'}</span>
          </div>
        )}
      </div>
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Modal: añadir línea con buscador de cotizaciones
// ─────────────────────────────────────────────────────────────────────

function AddLineaModal({ pedidoClienteId, clienteCodigo, onClose }: { pedidoClienteId: string; clienteCodigo: string; onClose: () => void }) {
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [seleccion, setSeleccion] = useState<Set<string>>(new Set());
  const cotiz = useQuery({
    queryKey: ['cotizaciones', 'recientes', clienteCodigo, q],
    queryFn: () => api.get<{ data: CotizacionBuscable[] }>(`/precio/cotizaciones/recientes?cliente_codigo=${clienteCodigo}&limit=50${q ? `&q=${encodeURIComponent(q)}` : ''}`),
  });
  const filtered = useMemo(() => cotiz.data?.data ?? [], [cotiz.data]);

  const toggle = (id: string) => {
    setSeleccion((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const addMasivo = useMutation({
    mutationFn: async () => {
      for (const id of seleccion) {
        await api.post(`/pedidos-cliente/${pedidoClienteId}/lineas`, { cotizacion_id: id, unidades: 1 });
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pedido-cliente', pedidoClienteId] });
      onClose();
    },
  });

  return (
    <Modal
      open
      onClose={onClose}
      title="Añadir líneas desde cotizaciones"
      size="xl"
      footer={
        <>
          <Button variant="danger" onClick={onClose}>Cancelar</Button>
          <Button variant="create" onClick={() => addMasivo.mutate()} disabled={seleccion.size === 0 || addMasivo.isPending}>
            {addMasivo.isPending ? 'Añadiendo…' : `+ Añadir ${seleccion.size} seleccionada${seleccion.size === 1 ? '' : 's'}`}
          </Button>
        </>
      }
    >
      <div className="mb-3">
        <div className="flex items-center gap-2">
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por SKU, nombre, ref proveedor…"
            autoFocus
            className="flex-1"
          />
          {seleccion.size > 0 && (
            <button type="button" onClick={() => setSeleccion(new Set())} className="cursor-pointer text-xs text-slate-500 hover:underline">
              Deseleccionar todas
            </button>
          )}
        </div>
        <p className="mt-0.5 text-[10px] leading-tight text-slate-400">
          Varias palabras = todas deben coincidir · <span className="font-medium">palabra1 / palabra2</span> = cualquiera coincide · <span className="font-medium">"frase exacta"</span> = coincidencia literal
        </p>
      </div>
      {cotiz.isLoading ? <Spinner /> : (
        <div className="max-h-[60vh] overflow-y-auto">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-600">
              <tr>
                <th className="px-2 py-1.5"></th>
                <th className="px-2 py-1.5 text-left">Fecha</th>
                <th className="px-2 py-1.5 text-left">SKU / Nombre</th>
                <th className="px-2 py-1.5 text-left">Categoría</th>
                <th className="px-2 py-1.5 text-right">Uds.</th>
                <th className="px-2 py-1.5 text-right">PVPr c/IVA</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.length === 0 && (
                <tr><td colSpan={6} className="py-6 text-center text-sm italic text-slate-400">Sin resultados.</td></tr>
              )}
              {filtered.map((c) => (
                <tr
                  key={c.id}
                  className={`cursor-pointer hover:bg-slate-50 ${seleccion.has(c.id) ? 'bg-sky-50' : ''}`}
                  onClick={() => toggle(c.id)}
                >
                  <td className="px-2 py-1">
                    <input type="checkbox" checked={seleccion.has(c.id)} onChange={() => toggle(c.id)} onClick={(e) => e.stopPropagation()} className="h-4 w-4 cursor-pointer" />
                  </td>
                  <td className="px-2 py-1 text-xs text-slate-500">{fmt.date(c.fecha)}</td>
                  <td className="px-2 py-1">
                    {c.producto_sku
                      ? <span className="font-mono text-xs">{c.producto_sku}</span>
                      : <span className="italic text-slate-500">{c.nombre_propuesto ?? c.ref_proveedor ?? '—'}</span>}
                    {c.producto_nombre && <div className="text-xs text-slate-500">{c.producto_nombre}</div>}
                  </td>
                  <td className="px-2 py-1 text-xs">{c.categoria_nombre ?? '—'}</td>
                  <td className="px-2 py-1 text-right font-mono text-xs">{c.unidades != null ? fmt.int(c.unidades) : '—'}</td>
                  <td className="px-2 py-1 text-right font-mono text-xs">{fmt.eur(c.pvpr_iva_eur ?? c.coste_game_final_eur)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────────────
// AddLineaManualModal: alta manual de línea sin pasar por cotización.
// Buscador contra productos de GAME, pregunta unidades + precio coste,
// y POSTea una línea con producto_id + sku_cliente + descripción
// pre-rellenadas a partir del producto elegido.
// ─────────────────────────────────────────────────────────────────────

type ProductoBusquedaRow = {
  id: string;
  sku: string;
  ean: string | null;
  nombre: string;
  coste: number | string | null;
  estado: string;
  cliente_codigo: string | null;
};

function AddLineaManualModal({
  pedidoClienteId, clienteCodigo, onClose,
}: { pedidoClienteId: string; clienteCodigo: string; onClose: () => void }) {
  const qc = useQueryClient();
  const productos = useQuery({
    queryKey: ['productos', 'listado-full', clienteCodigo],
    queryFn: () => api.get<{ data: ProductoBusquedaRow[] }>(`/productos/listado/full?cliente_codigo=${encodeURIComponent(clienteCodigo)}`),
  });
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [unidades, setUnidades] = useState<string>('1');
  const [precio, setPrecio] = useState<string>('');

  const filtradas = useMemo(() => {
    const all = productos.data?.data ?? [];
    const q = query.trim().toLowerCase();
    if (!q) return all.slice(0, 100);
    return all.filter((p) =>
      p.sku.toLowerCase().includes(q)
      || (p.ean?.toLowerCase().includes(q))
      || p.nombre.toLowerCase().includes(q),
    ).slice(0, 200);
  }, [productos.data, query]);

  const seleccionado = useMemo(() => filtradas.find((p) => p.id === selectedId) ?? (productos.data?.data ?? []).find((p) => p.id === selectedId), [filtradas, productos.data, selectedId]);

  // Cuando se selecciona, sugerimos el coste como precio default.
  const elegir = (p: ProductoBusquedaRow) => {
    setSelectedId(p.id);
    if (!precio && p.coste != null) setPrecio(String(p.coste));
  };

  const guardar = useMutation({
    mutationFn: () => {
      if (!seleccionado) throw new Error('Elige un producto.');
      const uds = Number(unidades);
      if (!Number.isFinite(uds) || uds <= 0) throw new Error('Unidades debe ser > 0.');
      const pr = precio ? Number(precio) : null;
      if (pr !== null && (!Number.isFinite(pr) || pr < 0)) throw new Error('Precio inválido.');
      return api.post(`/pedidos-cliente/${pedidoClienteId}/lineas`, {
        producto_id: seleccionado.id,
        sku_cliente: seleccionado.sku,
        ean: seleccionado.ean,
        descripcion: seleccionado.nombre,
        unidades: uds,
        precio_unitario: pr,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pedido-cliente'] });
      qc.invalidateQueries({ queryKey: ['pedidos-cliente'] });
      qc.invalidateQueries({ queryKey: ['pedidos-cliente-lineas'] });
      onClose();
    },
    onError: (err) => toast.error('No se pudo añadir la línea', (err as Error).message),
  });

  return (
    <Modal
      open
      onClose={onClose}
      title="Añadir producto manual al pedido"
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={guardar.isPending}>Cancelar</Button>
          <Button
            variant="create"
            onClick={() => guardar.mutate()}
            disabled={guardar.isPending || !seleccionado || !unidades || Number(unidades) <= 0}
          >
            {guardar.isPending ? 'Añadiendo…' : '+ Añadir línea'}
          </Button>
        </>
      }
    >
      <div className="space-y-3 text-sm">
        <p className="text-xs text-slate-500">
          Busca el producto en el catálogo {clienteCodigo}. Si no existe aún, créalo primero en {clienteCodigo} → Productos.
        </p>
        <FormRow label="Buscar por SKU, EAN o nombre">
          <Input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Ej: 258449, EAN, nombre…"
          />
        </FormRow>
        <div className="max-h-64 overflow-y-auto rounded-md border border-slate-200">
          {productos.isLoading ? (
            <p className="p-3 text-xs text-slate-500">Cargando catálogo…</p>
          ) : filtradas.length === 0 ? (
            <p className="p-3 text-xs italic text-slate-400">Sin resultados.</p>
          ) : (
            <table className="min-w-full text-xs">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-2 py-1.5 text-left">SKU</th>
                  <th className="px-2 py-1.5 text-left">EAN</th>
                  <th className="px-2 py-1.5 text-left">Nombre</th>
                  <th className="px-2 py-1.5 text-right">Coste BD</th>
                  <th className="px-2 py-1.5"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtradas.map((p) => (
                  <tr key={p.id} className={p.id === selectedId ? 'bg-emerald-50' : 'hover:bg-slate-50'}>
                    <td className="px-2 py-1.5 font-mono">{p.sku}</td>
                    <td className="px-2 py-1.5 font-mono text-slate-500">{p.ean ?? '—'}</td>
                    <td className="px-2 py-1.5">{p.nombre}</td>
                    <td className="px-2 py-1.5 text-right font-mono">{fmt.eur(p.coste)}</td>
                    <td className="px-2 py-1.5 text-right">
                      <Button
                        size="sm"
                        variant={p.id === selectedId ? 'save' : 'edit'}
                        onClick={() => elegir(p)}
                      >
                        {p.id === selectedId ? '✓ Elegido' : 'Elegir'}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        {seleccionado && (
          <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3">
            <p className="text-xs font-semibold text-emerald-900">Producto elegido:</p>
            <p className="text-sm text-slate-900">
              <span className="font-mono">{seleccionado.sku}</span> · {seleccionado.nombre}
            </p>
          </div>
        )}
        <div className="grid gap-3 sm:grid-cols-2">
          <FormRow label="Unidades" required>
            <Input
              type="number"
              min={1}
              value={unidades}
              onChange={(e) => setUnidades(e.target.value)}
            />
          </FormRow>
          <FormRow label="Precio coste / unidad (EUR)" hint="Pre-rellenado con el coste del catálogo si lo había.">
            <Input
              type="number"
              step="any"
              value={precio}
              onChange={(e) => setPrecio(e.target.value)}
              placeholder="Ej: 85.42"
            />
          </FormRow>
        </div>
      </div>
    </Modal>
  );
}

/**
 * Resumen del pedido, siempre a la vista sobre las pestañas (CEO, 26/10/2026):
 * referencias, unidades, importe sin y con IVA y fecha estimada de entrega.
 * Cálculo en shared/resumen-pedido.ts; el IVA es `iva_general_pct`.
 */
function ResumenPedidoBanda({ lineas }: { lineas: LineaRow[] }) {
  const iva = useQuery({
    queryKey: ['list', '/admin/parametros', 'iva_general_pct'],
    queryFn: () => api.get<{ valor: string }>('/admin/parametros/iva_general_pct'),
    staleTime: 5 * 60_000,
  });
  const ivaPct = iva.data?.valor != null && Number.isFinite(Number(iva.data.valor)) ? Number(iva.data.valor) : 21;
  const r = resumenPedido(lineas, ivaPct);
  const e = r.entrega;
  const entrega = e.tipo === 'entregado'
    ? { valor: 'Entregado', nota: e.fecha ? `última entrega el ${fmt.date(e.fecha)}` : 'todas las líneas entregadas', tono: 'text-emerald-700' }
    : e.tipo === 'sin_fecha'
      ? { valor: 'Sin fecha', nota: 'ninguna línea pendiente tiene ETA ni entrega solicitada', tono: 'text-slate-400' }
      : {
        valor: fmt.date(e.fecha) ?? '—',
        nota: [
          e.fuente === 'estimada' ? 'entrega estimada de la última línea pendiente'
            : e.fuente === 'eta' ? 'ETA de la última línea pendiente'
            : e.fuente === 'solicitada' ? 'entrega solicitada (sin ETA)'
            : 'entrega estimada, ETA o solicitada',
          e.primera ? `primera ${fmt.date(e.primera)}` : null,
          e.sinFecha ? `${e.sinFecha} línea(s) sin fecha` : null,
        ].filter(Boolean).join(' · '),
        tono: 'text-slate-900',
      };
  return (
    <div className="mb-4 grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-slate-200 bg-slate-200 shadow-sm sm:grid-cols-3 lg:grid-cols-5">
      <Cifra label="Referencias" valor={fmt.int(r.referencias)} nota={r.lineas !== r.referencias ? `${fmt.int(r.lineas)} líneas` : 'productos distintos'} />
      <Cifra label="Unidades totales" valor={fmt.int(r.unidades)} nota={r.unidadesEntregadas ? `${fmt.int(r.unidadesEntregadas)} entregadas` : 'ninguna entregada'} />
      <Cifra label="Importe sin IVA" valor={fmt.eur(r.importeSinIva)} />
      <Cifra label="Importe con IVA" valor={fmt.eur(r.importeConIva)} nota={`IVA ${fmt.int(r.ivaPct)} %`} destacado />
      <Cifra label="Fecha estimada de entrega" valor={entrega.valor} nota={entrega.nota} tono={entrega.tono} />
    </div>
  );
}

function Cifra({ label, valor, nota, tono = 'text-slate-900', destacado = false }: { label: string; valor: ReactNode; nota?: string; tono?: string; destacado?: boolean }) {
  return (
    <div className={`min-w-0 px-4 py-3 ${destacado ? 'bg-accent-50' : 'bg-white'}`}>
      <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`mt-0.5 font-display text-2xl font-extrabold leading-tight tabular-nums ${destacado ? 'text-accent-700' : tono}`}>{valor}</div>
      {nota && <div className="mt-0.5 truncate text-xs text-slate-500" title={nota}>{nota}</div>}
    </div>
  );
}

/**
 * Registro de fechas (CEO, 26/10/2026): por pedido y por línea, cada fecha
 * (ETD, ETA, entrega estimada, solicitada) con sus valores anteriores tachados
 * y el vigente al final, y cuántas veces se ha reprogramado.
 */
function RegistroFechasCard({ pedido, historial, cargando }: { pedido: PedidoClienteFull; historial: HistorialFecha[]; cargando: boolean }) {
  const nombreLinea = (id: string) => {
    const l = pedido.lineas.find((x) => x.id === id);
    return l ? `${l.sku_cliente ?? l.producto_sku ?? '—'}${l.producto_nombre ?? l.descripcion ? ` · ${l.producto_nombre ?? l.descripcion}` : ''}` : 'Línea eliminada';
  };
  const ordenLinea = (id: string | null) => (id == null ? -1 : pedido.lineas.findIndex((l) => l.id === id));
  const filas = [...historial].sort((a, b) => ordenLinea(a.linea_id) - ordenLinea(b.linea_id)
    || CAMPOS_FECHA.indexOf(a.campo) - CAMPOS_FECHA.indexOf(b.campo));
  const total = historial.reduce((a, h) => a + h.cambios, 0);
  const VIA = { proveedor: 'desde el pedido a proveedor', cabecera: 'aplicada desde la cabecera' } as const;

  return (
    <Card
      title="Registro de fechas"
      description="Cada vez que se cambia una fecha que ya tenía valor, la anterior queda tachada y la nueva detrás. Sale del log de modificaciones del pedido y de sus pedidos a proveedor."
    >
      {cargando ? <Spinner /> : filas.length === 0 ? (
        <p className="text-sm italic text-slate-400">Aún no hay fechas registradas en este pedido.</p>
      ) : (
        <>
          <p className="mb-3 text-sm text-slate-600">
            <span className="font-semibold text-slate-900">{fmt.int(total)}</span> reprogramación(es) en {fmt.int(filas.filter((h) => h.cambios > 0).length)} fecha(s).
          </p>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-600">
                <tr>
                  <th className="px-2 py-1.5 text-left">Ámbito</th>
                  <th className="px-2 py-1.5 text-left">Fecha</th>
                  <th className="px-2 py-1.5 text-left">Historial</th>
                  <th className="px-2 py-1.5 text-right">Cambios</th>
                  <th className="px-2 py-1.5 text-left">Último cambio</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filas.map((h) => {
                  const ultimo = h.valores.at(-1)!;
                  return (
                    <tr key={`${h.linea_id ?? 'pedido'}-${h.campo}`} className={`align-top ${h.cambios > 0 ? '' : 'text-slate-500'}`}>
                      <td className="px-2 py-1.5 text-xs">{h.linea_id == null ? <span className="font-semibold">Pedido</span> : <span className="font-mono">{nombreLinea(h.linea_id)}</span>}</td>
                      <td className="whitespace-nowrap px-2 py-1.5 text-xs font-semibold">{ETIQUETA_CAMPO[h.campo]}</td>
                      <td className="px-2 py-1.5 text-xs">
                        <span className="inline-flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
                          {h.valores.map((v, i) => {
                            const vigente = i === h.valores.length - 1;
                            const titulo = v.fecha_hora
                              ? `Puesta el ${fmt.datetime(v.fecha_hora)}${v.usuario_email ? ` por ${v.usuario_email}` : ''}${v.via ? ` (${VIA[v.via]})` : ''}`
                              : 'Valor inicial';
                            return (
                              <Fragment key={i}>
                                {i > 0 && <span className="text-slate-300">→</span>}
                                <span title={titulo} className={vigente ? 'font-semibold text-slate-900' : 'text-slate-400 line-through'}>
                                  {v.valor ? fmt.date(v.valor) : 'sin fecha'}
                                </span>
                              </Fragment>
                            );
                          })}
                        </span>
                      </td>
                      <td className="px-2 py-1.5 text-right font-mono text-xs">
                        {h.cambios > 0 ? <span className={h.cambios >= 3 ? 'font-semibold text-rose-600' : 'font-semibold text-amber-700'}>{fmt.int(h.cambios)}</span> : '0'}
                      </td>
                      <td className="whitespace-nowrap px-2 py-1.5 text-xs text-slate-500">
                        {ultimo.fecha_hora ? <>{fmt.datetime(ultimo.fecha_hora)}{ultimo.usuario_email ? ` · ${ultimo.usuario_email}` : ''}{ultimo.via ? ` · ${VIA[ultimo.via]}` : ''}</> : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Card>
  );
}
